import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, Navigate, Route, Routes, useNavigate, useParams } from "react-router";
import {
  Button,
  Card,
  Checkbox,
  Cluster,
  FormField,
  Grid,
  Input,
  PasswordInput,
  Textarea,
  Modal,
  NextBillNote,
  ProgressBar,
  Select,
  Stack,
  guardSubmit,
} from "@salesapp/ui";
import { Flash } from "./Flash";
import { AdminNav, PLATFORM_ADMIN_NAV } from "./AdminNav";
import { useIdleTimeout } from "./useIdleTimeout";
import { api, ApiError, holdSessionRefresh, SESSION_EXPIRED_EVENT } from "./api";
import { TipHeading, TipKicker } from "./AdminTip";
import { CurrentOnlineUsersPage, DashboardPage } from "./DashboardPage";
import { ThreatsPage } from "./ThreatsPage";
import { MarketingMetricsPage } from "./MarketingMetricsPage";
import { PaymentsPage } from "./PaymentsPage";
import { AccessPage } from "./AccessPage";
import { WebsitePage } from "./WebsitePage";
import { BackupsPage } from "./BackupsPage";
import { CapacityPage } from "./CapacityPage";
import { EmailErrorsPage } from "./EmailErrorsPage";
import { MonitoringPage } from "./MonitoringPage";
import { StorageAccessPage } from "./StorageAccessPage";
import { AdminErrorPage } from "./ErrorPage";
import { MailTemplatesPage } from "./MailTemplatesPage";
import { CustomerAlertsCard } from "./CustomerAlertsCard";
import { TrialEmailPlan, type TrialEmailPlanData } from "./TrialEmailPlan";
import { PrivacyPage } from "./PrivacyPage";
import { DependencySecurityPage } from "./DependencySecurityPage";
import { MaintenancePage } from "./MaintenancePage";
import { OrgAiBudgetCard } from "./OrgAiBudgetCard";
import { AllowanceCalculator } from "./AllowanceCalculator";
import { AiPricingGuidePage } from "./AiPricingGuidePage";
import { RecycleBinPage } from "./RecycleBinPage";
import { AiPage } from "./AiPage";
import { StaffActivityPage } from "./StaffActivityPage";
import { notifySaved } from "./savedToast";
import { AiBillingCard, ExpiryField, ExpiryNote, MailboxAiCard, expiryInputValue, expiryPayload } from "./AiAddonsBilling";
import { ThemeToggle } from "./ThemeToggle";
import { WorldClocks } from "./WorldClocks";
import { ALL_COUNTRY_CODES, COUNTRY_CURRENCY } from "./countries";
import { countryLabel, formatAiUsd, formatBytes, formatCustomerSince, formatDateUtc, formatDurationMs, formatFxStatus, formatUtc, money } from "./format";

interface Session {
  user_id: string;
  email: string;
  is_platform_staff: boolean;
  /** "full" sees everything; "marketing" sees only the Marketing tab. The API enforces this too. */
  staff_scope?: "full" | "marketing";
}

interface LicenseRow {
  app_code: string;
  tier: string;
  seats: number;
  status: string;
  grant_source?: string;
  access_starts_at?: string | null;
  access_ends_at: string | null;
  billing_due_at?: string | null;
  billing_interval?: string | null;
}

interface MemberRow {
  user_id: string;
  email: string;
  display_name: string | null;
  user_status: string;
  is_org_admin: boolean;
  is_app_admin?: boolean;
  role_names?: string[];
  status: string;
  membership_status?: string;
  last_login_at?: string | null;
}

function memberRoleLabel(member: MemberRow): string {
  if (member.is_org_admin) return "Org admin";
  const roles = [...(member.is_app_admin ? ["App admin"] : []), ...(member.role_names ?? [])];
  return roles.length ? roles.join(", ") : "No role";
}

interface OpenWorkItem {
  key: string;
  label: string;
  count: number;
}

interface SalesManagerOption {
  user_id: string;
  display_name: string;
  email: string;
}

interface ReassignPrompt {
  userId: string;
  name: string;
  items: OpenWorkItem[];
  managers: SalesManagerOption[];
  managerId: string;
}

function readReassign(err: ApiError): { items: OpenWorkItem[]; sales_managers: SalesManagerOption[] } | null {
  if (err.code !== "reassign_required") return null;
  const body = err.body as { detail?: { items?: OpenWorkItem[]; sales_managers?: SalesManagerOption[] } } | null;
  const detail = body?.detail;
  if (!detail || !Array.isArray(detail.items) || !Array.isArray(detail.sales_managers)) return null;
  return { items: detail.items, sales_managers: detail.sales_managers };
}

interface SetupLinkResult {
  setup_token: string;
  email: string;
  emailed: boolean;
  delivery: "sent" | "copied" | "not_configured" | "failed";
}

interface EventRow {
  kind: string;
  app_code: string | null;
  created_at: string | null;
  actor_email?: string | null;
  actor_name?: string | null;
  payload?: Record<string, unknown> | null;
}

interface PlanHistoryRow {
  kind: string;
  event_id?: string | null;
  from_tier?: string | null;
  to_tier?: string | null;
  seats?: number | null;
  source?: string | null;
  created_at: string | null;
  actor_email?: string | null;
  actor_name?: string | null;
  discount_code?: string | null;
  percent_applied?: number | null;
  discount_kind?: string | null;
  discount_value?: number | null;
  discount_months?: number | null;
  list_cents?: number | null;
  pay_cents?: number | null;
  currency?: string | null;
  interval?: string | null;
  amount_cents?: number | null;
  period_end?: string | null;
  effective_at?: string | null;
  access_until?: string | null;
  scheduled?: boolean | null;
  reason?: string | null;
  error_message?: string | null;
  invoice_id?: string | null;
  payment_intent_id?: string | null;
  charge_id?: string | null;
  pf_payment_id?: string | null;
  payfast_subscription_payment_id?: string | null;
  payfast_subscription_token?: string | null;
  refund_id?: string | null;
  refunded_payment_event_id?: string | null;
  submitted_request?: Record<string, unknown> | null;
  received_response?: Record<string, unknown> | null;
  upgrade_charged_now?: boolean | null;
  billing_effective_at?: string | null;
  prorated_months?: number | null;
  proration_formula?: string | null;
  proration_current_annual_cents?: number | null;
  proration_new_annual_cents?: number | null;
  proration_subtotal_cents?: number | null;
  proration_tax_cents?: number | null;
  proration_total_cents?: number | null;
  tax_cents?: number | null;
  total_cents?: number | null;
  tax?: { label?: string; percent?: number } | null;
}

interface MoneyPrice {
  currency: string;
  amount_cents: number;
  amount: string;
}

interface PlanRow {
  app_code: string;
  tier: string;
  display_name?: string;
  amount_cents: number;
  trial_days: number;
  seats: number;
  min_seats?: number;
  max_seats?: number | null;
  billing_unit?: string;
  currency?: string;
  amount?: string;
  local_currency?: string;
  local_amount?: string;
  prices?: Record<string, MoneyPrice>;
  campaign?: CampaignOffer | null;
}

interface CampaignOffer {
  id: string | null;
  name: string;
  hook: string;
  discount_kind: string;
  discount_value: number;
  interval: string;
  monthly: MoneyPrice;
  yearly_list: MoneyPrice;
  pay_now: MoneyPrice;
  save: MoneyPrice;
}

interface CampaignRow {
  id: string;
  name: string;
  hook: string;
  discount_kind: string;
  discount_value: number;
  interval: string;
  app_code: string;
  tier: string | null;
  active: boolean;
  is_default?: boolean;
  starts_at?: string | null;
  ends_at?: string | null;
}

type CampaignDraft = {
  name: string;
  hook: string;
  discount_kind: string;
  discount_value: string;
};

const CAMPAIGN_PLAN_OPTIONS: Array<{ id: string; label: string }> = [
  { id: "starter", label: "Basic" },
  { id: "professional", label: "Pro" },
  { id: "enterprise", label: "Enterprise" },
];

const DEFAULT_CAMPAIGN_DRAFTS: Record<string, CampaignDraft> = {
  starter: {
    name: "Basic yearly default",
    hook: "Sign up today for 1 year and get 1 month free",
    discount_kind: "free_months",
    discount_value: "1",
  },
  professional: {
    name: "Pro yearly default",
    hook: "Sign up today for 1 year and get 2 months free",
    discount_kind: "free_months",
    discount_value: "2",
  },
  enterprise: {
    name: "Enterprise yearly default",
    hook: "Sign up for the Enterprise plan and save 10%",
    discount_kind: "percent_off",
    discount_value: "10",
  },
};

const BILLING_COPY_NOTE =
  "Renewals: a 20% off code discounts one payment only. A year later the bill is full list price, not 20% off. Same for month-to-month 5%: the next charge is full price, even if you later delete the campaign or the code. Campaigns are marketing copy only; checkout only changes when a discount code is entered.";

const PLAN_CARD_FEATURES: Record<string, string[]> = {
  starter: [
    "Companies, contacts, and activity",
    "Lead capture",
    "Products",
    "Deal board",
    "Dashboards",
    "One territory",
  ],
  professional: [
    "Everything in Basic",
    "Stalled-deal alerts",
    "Workflow automation",
    "Quotes and orders",
    "Quote and order templates",
    "Sales-cycle checklists",
    "Forecast and support tickets",
  ],
  enterprise: [
    "Everything in Pro",
    "Invoices",
    "Custom screens (app designer)",
    "Extra sales territories",
    "IP allowlist for tenant access",
    "Timesheet",
    "Expense Manager",
  ],
};

const PLAN_CARD_TAGLINES: Record<string, string> = {
  starter: "Pipeline without the spreadsheet.",
  professional: "Catch quiet deals before the quarter slips.",
  enterprise: "Quote, order, and invoice in one place.",
};

function campaignIsDefault(row: CampaignRow): boolean {
  return Boolean(row.is_default) || (!row.starts_at && !row.ends_at);
}

function campaignIsLive(row: CampaignRow, now = Date.now()): boolean {
  if (!row.active) return false;
  if (row.starts_at && Date.parse(row.starts_at) > now) return false;
  if (row.ends_at && Date.parse(row.ends_at) <= now) return false;
  return true;
}

function matchLiveCampaign(campaigns: CampaignRow[], tier: string): CampaignRow | null {
  const live = campaigns.filter((row) => campaignIsLive(row));
  const overlays = live.filter((row) => !campaignIsDefault(row));
  const defaults = live.filter((row) => campaignIsDefault(row));
  return (
    overlays.find((row) => row.tier === tier)
    ?? overlays.find((row) => !row.tier)
    ?? defaults.find((row) => row.tier === tier)
    ?? defaults.find((row) => !row.tier)
    ?? null
  );
}

function utcDateInput(daysFromToday = 0): string {
  const stamp = new Date();
  stamp.setUTCDate(stamp.getUTCDate() + daysFromToday);
  return stamp.toISOString().slice(0, 10);
}

function isoDateInput(value: string | null | undefined): string {
  return value ? value.slice(0, 10) : "";
}

function previewMoney(plan: PlanRow): string {
  const aud = plan.prices?.AUD;
  if (aud) return money(aud.amount_cents, "AUD");
  return money(plan.amount_cents, plan.currency || "USD");
}

function discountOfferLabel(
  row: Pick<DiscountCodeRow, "discount_kind" | "discount_value" | "discount_months">,
): string {
  if (row.discount_kind === "free_months") return `Buy 12, get ${row.discount_value} free`;
  return row.discount_months
    ? `${row.discount_value}% off for ${row.discount_months} months`
    : `${row.discount_value}% off`;
}

function startOfUtcDay(value: string): string | null {
  return value ? `${value}T00:00:00.000Z` : null;
}

function endOfUtcDay(value: string): string | null {
  return value ? `${value}T23:59:59.000Z` : null;
}

function campaignOfferLabel(row: Pick<CampaignRow, "discount_kind" | "discount_value" | "interval">): string {
  const offer = row.discount_kind === "free_months"
    ? `Buy 12, get ${row.discount_value} free`
    : `${row.discount_value}% off`;
  return `${offer} · ${row.interval}`;
}

interface DiscountCodeRow {
  id: string;
  code: string;
  name: string;
  hook: string;
  bound_email?: string | null;
  discount_kind: string;
  discount_value: number;
  discount_months?: number | null;
  applies_starter: boolean;
  applies_professional: boolean;
  applies_enterprise: boolean;
  tiers?: string[];
  active: boolean;
  starts_at?: string | null;
  ends_at?: string | null;
  redeemed_count: number;
}

interface PlanCopyStatus {
  source: "published" | "fallback";
  published_at?: string | null;
  unpublished_changes?: boolean;
  payload?: { plans?: Record<string, { hook?: string; name?: string; discount_kind?: string; discount_value?: number }> };
  draft?: { plans?: Record<string, { hook?: string; name?: string; discount_kind?: string; discount_value?: number }> };
}

interface FxRates {
  base: string;
  rates: Record<string, number>;
  source?: string;
  as_of?: string | null;
  updated_at?: string | null;
}

interface BillingMarket {
  country_code: string;
  name: string;
  currency: string;
  tax_label: string;
  tax_percent: number | string;
}

interface SignupAttribution {
  source?: string | null;
  medium?: string | null;
  campaign?: string | null;
  content?: string | null;
  landing?: string | null;
  referrer_host?: string | null;
}

interface WebsiteVisitPage {
  path: string;
  duration_ms: number;
}

interface WebsiteVisit {
  session_id: string;
  source_ip?: string | null;
  country?: string | null;
  started_at?: string | null;
  total_ms: number;
  landing_path?: string | null;
  pages: WebsiteVisitPage[];
}

interface EmailLogRow {
  template_key: string;
  to_email: string;
  subject: string;
  html_body: string;
  sent_at: string | null;
}

interface OrgUsage {
  seats: { licensed: number; assigned: number; fill_pct: number };
  ai: {
    used_cents: number;
    budget_cents: number;
    remaining_cents: number;
    enabled: boolean;
    provider: string;
  };
  records: Record<string, number>;
  records_total: number;
  storage: { bytes: number; by_area: Record<string, number> };
}

interface OrgDetail {
  org_id: string;
  display_name: string;
  slug: string;
  status: string;
  billing_email: string | null;
  welcome_email_sent?: boolean;
  welcome_email_sent_at?: string | null;
  trial_nurture?: Record<string, string | null>;
  trial_nurture_sources?: Record<string, "sent" | "purchase" | "manual">;
  trial_email_plan?: TrialEmailPlanData;
  cancelled_at: string | null;
  purge_requested_at: string | null;
  purged_at: string | null;
  created_at?: string | null;
  customer_since?: string | null;
  current_plan?: LicenseRow | null;
  licenses: LicenseRow[];
  members: MemberRow[];
  events: EventRow[];
  plan_history?: PlanHistoryRow[];
  discount_codes?: string[];
  plans: PlanRow[];
  next_bill?: {
    amount_cents: number;
    amount: string;
    currency: string;
    billed_at: string;
    payment_process_at?: string;
    renewal_at?: string;
    payment_lead_days?: number;
    interval: string;
    seat_packs: number;
    pack_seats: number;
    licensed_seats: number;
  } | null;
  created_member?: { setup_token?: string; email: string };
  ai: { monthly_budget_cents: number; used_cents: number; provider: string; enabled: boolean; remaining_cents: number };
  usage?: OrgUsage | null;
  payment_method?: {
    brand?: string | null;
    last4?: string | null;
  } | null;
  signup_attribution?: SignupAttribution | null;
  website_visit?: WebsiteVisit | null;
}

interface OrgRow {
  org_id: string;
  display_name: string;
  slug: string;
  status: string;
  members: number;
  billing_email: string | null;
  cancelled_at?: string | null;
  purge_requested_at?: string | null;
  created_at?: string | null;
  license_type?: "trial" | "paid" | "exempt" | null;
  licenses: LicenseRow[];
  signup_attribution?: SignupAttribution | null;
  discount_codes?: string[];
  storage_bytes?: number;
  storage_mb?: number;
}

type CustomerSortKey = "organization" | "slug" | "status" | "created" | "people" | "storage" | "licences" | "license_type" | "source" | "discount";

function orgConsoleOrigin(): string {
  const { protocol, hostname, port } = window.location;
  if (port === "5574") return `${protocol}//${hostname}:5573`;
  if (hostname === "dev-admin.saleslox.com") return `${protocol}//dev-app.saleslox.com`;
  if (hostname === "1979s.saleslox.com") return `${protocol}//app.saleslox.com`;
  return window.location.origin;
}

function setupPasswordUrl(token: string): string {
  return `${orgConsoleOrigin()}/setup-password?token=${encodeURIComponent(token)}`;
}

interface PlatformAi {
  provider: string;
  enabled: boolean;
  default_monthly_budget_cents: number;
}

interface PaymentsToggle {
  accepted: boolean;
  reason: string | null;
  payment_lead_days: number;
  updated_at: string | null;
  updated_by: string | null;
}

interface PayfastStatus {
  configured: boolean;
  enabled: boolean;
  ready: boolean;
  payments_allowed: boolean;
  sandbox: boolean;
}

export function App() {
  const [session, setSession] = useState<Session | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  async function loadSession() {
    try {
      const row = await api.get<Session>("/auth/session");
      if (!row.is_platform_staff) {
        setError("This console is for platform staff.");
        setSession(null);
        return;
      }
      setSession(row);
      setError(null);
    } catch {
      setSession(null);
    } finally {
      setReady(true);
    }
  }

  useEffect(() => {
    void loadSession();
  }, []);

  useEffect(() => {
    function onSessionExpired(event: Event) {
      const code = (event as CustomEvent<{ code: string | null }>).detail?.code;
      setSession(null);
      setError(
        code === "session_replaced"
          ? "This account signed in on another computer. Sign in again to continue."
          : code === "session_idle"
            ? "You were logged out from an idle session. Sign in again to continue."
            : "Your session ended. Sign in again to continue.",
      );
    }
    window.addEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
  }, []);

  // Revoke server-side when possible, but always clear the screen: an idle
  // admin console must not leave tenant data visible.
  useIdleTimeout(session !== null, () => {
    setSession(null);
    setError("You were logged out from an idle session. Sign in again to continue.");
    void api.post("/auth/logout", {}).catch(() => undefined);
  });

  if (!ready) {
    return (
      <main className="admin-frame">
        <p>Loading…</p>
      </main>
    );
  }

  if (!session) {
    return <LoginForm error={error} onSignedIn={(row) => {
      setSession(row);
      setError(null);
    }} />;
  }

  if (session.staff_scope === "marketing") {
    return (
      <div className="admin-shell">
        <header className="admin-top">
          <Cluster gap={3} align="center">
            <img src="/logo-mark.svg" alt="" className="admin-brand-mark" />
            <Stack gap={2}>
              <p className="admin-kicker">Platform admin</p>
              <h1>Saleslox</h1>
            </Stack>
          </Cluster>
          <Cluster gap={3} align="center">
            <span className="admin-session-email">{session.email}</span>
            <Button variant="ghost" onClick={() => api.post("/auth/logout", {}).then(() => setSession(null))}>
              Sign out
            </Button>
          </Cluster>
        </header>
        <nav className="admin-nav" aria-label="Platform admin">
          <NavLink to="/marketing" className={({ isActive }) => (isActive ? "is-active" : undefined)}>
            Marketing
          </NavLink>
        </nav>
        <main className="admin-frame">
          <Routes>
            <Route path="/marketing" element={<MarketingMetricsPage />} />
            <Route path="*" element={<Navigate to="/marketing" replace />} />
          </Routes>
        </main>
      </div>
    );
  }

  return (
    <div className="admin-shell">
      <header className="admin-top">
        <Cluster gap={3} align="center">
          <img src="/logo-mark.svg" alt="" className="admin-brand-mark" />
          <Stack gap={2}>
            <p className="admin-kicker">Platform admin</p>
            <h1>Saleslox</h1>
            <p className="admin-muted admin-top-note">Tenants, licences, annuity, and platform health. Customer CRM work stays on the org console.</p>
          </Stack>
        </Cluster>
        <WorldClocks />
        <Cluster gap={3} align="center">
          <span className="admin-session-email">{session.email}</span>
          <Button
            variant="ghost"
            onClick={() => api.post("/auth/logout", {}).then(() => setSession(null))}
          >
            Sign out
          </Button>
        </Cluster>
      </header>
      <AdminNav label="Platform admin" items={PLATFORM_ADMIN_NAV} />
      <main className="admin-frame">
        <Routes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/website" element={<WebsitePage />} />
          <Route path="/threats" element={<ThreatsPage />} />
          <Route path="/marketing" element={<MarketingMetricsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/security" element={<AccessPage />} />
          <Route path="/access" element={<AccessPage />} />
          <Route path="/monitoring" element={<MonitoringPage />} />
          <Route path="/backups" element={<BackupsPage />} />
          <Route path="/capacity" element={<CapacityPage />} />
          <Route path="/storage-access" element={<StorageAccessPage />} />
          <Route path="/dependency-security" element={<DependencySecurityPage />} />
          <Route path="/customers" element={<CustomersPage />} />
          <Route path="/customers/:orgId" element={<CustomerDetailPage />} />
          <Route path="/recycle-bin" element={<RecycleBinPage />} />
          <Route path="/payments" element={<PaymentsPage />} />
          <Route path="/mail" element={<MailTemplatesPage />} />
          <Route path="/email-errors" element={<EmailErrorsPage />} />
          <Route path="/catalogue" element={<CataloguePage />} />
          <Route path="/platform" element={<PlatformPage />} />
          <Route path="/ai" element={<AiPage />} />
          <Route path="/ai/pricing-guide" element={<AiPricingGuidePage />} />
          <Route path="/users" element={<UsersPage />} />
          <Route path="/activity" element={<StaffActivityPage />} />
          <Route path="/online-users" element={<CurrentOnlineUsersPage />} />
          <Route path="/maintenance" element={<MaintenancePage />} />
          <Route path="/error/:code" element={<AdminErrorPage />} />
          <Route path="*" element={<AdminErrorPage code={404} />} />
        </Routes>
      </main>
    </div>
  );
}

function LoginForm({ error, onSignedIn }: { error: string | null; onSignedIn: (row: Session) => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [formError, setFormError] = useState<string | null>(error);
  const [submitting, setSubmitting] = useState(false);
  const shownError = formError || error;

  async function completeLogin() {
    const releaseRefresh = holdSessionRefresh();
    try {
      const result = await api.post<Session>("/auth/login", {
        email,
        password,
      });
      if (!result.is_platform_staff) {
        setFormError("This console is for platform staff.");
        return;
      }
      onSignedIn({
        user_id: result.user_id,
        email: result.email,
        is_platform_staff: true,
        staff_scope: result.staff_scope === "marketing" ? "marketing" : "full",
      });
    } finally {
      releaseRefresh();
    }
  }

  async function login(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      await completeLogin();
    } catch (err) {
      const allowed = new Set([
        "Invalid email or password",
        "Too many sign-in attempts",
        "Access from this network is not allowed.",
        "This organization can only be used from approved networks.",
      ]);
      if (err instanceof ApiError && err.status >= 400 && err.status < 500 && allowed.has(err.message)) {
        setFormError(err.message);
      } else {
        setFormError("Unable to log you in due to a network issue.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="login-page-frame">
      <ThemeToggle />
      <Card padding="lg" className="login-card">
        <Stack gap={5}>
          <Stack gap={2} align="center">
            <img src="/logo-mark.svg" alt="" className="login-brand-mark" />
            <h1 className="login-brand-title">Saleslox</h1>
            <p className="login-brand-subtitle">Sign in to platform admin</p>
          </Stack>
          {shownError && <p className="login-error" role="alert">{shownError}</p>}
          <Stack as="form" gap={4} onSubmit={guardSubmit(login)}>
            <FormField
              label="Email"
              htmlFor="admin-email"
              required
              tooltip="Platform staff email. Org-console accounts cannot sign in here."
            >
              <Input
                id="admin-email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
              />
            </FormField>
            <FormField
              label="Password"
              htmlFor="admin-password"
              required
              tooltip="Password for this platform-staff account."
            >
              <PasswordInput
                id="admin-password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
              />
            </FormField>
            <Button type="submit" fullWidth disabled={submitting}>
              {submitting ? "Signing in..." : "Sign in"}
            </Button>
          </Stack>
        </Stack>
      </Card>
    </div>
  );
}

interface PlatformUserRow {
  user_id: string;
  email: string;
  display_name: string;
  status: string;
  mfa_required: boolean;
  mfa_channel: string | null;
  staff_scope?: "full" | "marketing";
  created_at: string | null;
  last_login_at: string | null;
}

function UsersPage() {
  const [users, setUsers] = useState<PlatformUserRow[]>([]);
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [deliver, setDeliver] = useState<"email" | "copy">("email");
  const [staffScope, setStaffScope] = useState<"full" | "marketing">("full");
  const [setupLink, setSetupLink] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  async function loadUsers() {
    setError(null);
    try {
      const row = await api.get<{ users: PlatformUserRow[] }>("/operator/platform-users");
      setUsers(row.users);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Admin users could not be loaded.");
    }
  }

  useEffect(() => {
    void loadUsers();
    void api.get<Session>("/auth/session").then((row) => setCurrentUserId(row.user_id)).catch(() => undefined);
  }, []);

  const selectableUsers = useMemo(
    () => users.filter((user) => user.user_id !== currentUserId && user.email.toLowerCase() !== "admin@saleslox.com"),
    [users, currentUserId],
  );
  const allSelected = selectableUsers.length > 0 && selectableUsers.every((user) => selected.has(user.user_id));

  function toggleOne(userId: string) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  }

  function toggleAll() {
    setSelected(allSelected ? new Set() : new Set(selectableUsers.map((user) => user.user_id)));
  }

  async function disableSelected() {
    setError(null);
    setBusy(true);
    try {
      for (const userId of selected) {
        await api.post(`/operator/platform-users/${userId}/status`, { status: "disabled" });
      }
      setSelected(new Set());
      await loadUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not disable the selected users.");
    } finally {
      setBusy(false);
    }
  }

  async function deleteSelected() {
    setError(null);
    setBusy(true);
    try {
      for (const userId of selected) {
        await api.delete(`/operator/platform-users/${userId}`);
      }
      setSelected(new Set());
      setConfirmDelete(false);
      await loadUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not delete the selected users.");
      setConfirmDelete(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Stack gap={4}>
      <TipHeading
        as="h2"
        label="Users"
        tip="Platform admin-console users. Every non-admin@saleslox.com admin is marked for email MFA."
      />
      {error && <Flash tone="error">{error}</Flash>}
      {notice && (
        <p className="admin-muted">
          {notice}
          {setupLink ? <> <code className="admin-setup-link">{setupLink}</code></> : null}
        </p>
      )}
      <Card>
        <Stack
          as="form"
          gap={3}
          onSubmit={guardSubmit((event) => {
            event.preventDefault();
            return (async () => {
              setError(null);
              setNotice(null);
              setSetupLink(null);
              try {
                const row = await api.post<{
                  setup_token: string;
                  emailed: boolean;
                  delivery: string;
                  user: PlatformUserRow;
                }>("/operator/platform-users", {
                  email,
                  display_name: displayName,
                  deliver,
                  staff_scope: staffScope,
                });
                setEmail("");
                setDisplayName("");
                setStaffScope("full");
                setNotice(
                  row.delivery === "sent"
                    ? `Setup email sent to ${row.user.email}.`
                    : `Created ${row.user.email}. Use this one-time setup link:`,
                );
                setSetupLink(setupPasswordUrl(row.setup_token));
                await loadUsers();
              } catch (err) {
                setError(err instanceof Error ? err.message : "Admin user could not be created.");
              }
            })();
          })}
        >
          <TipHeading
            label="Add admin user"
            tip="Creates an invited platform-staff user and issues the same password setup link used elsewhere."
          />
          <Cluster gap={3} align="end">
            <FormField label="Email" htmlFor="platform-user-email" tooltip="Login email for the admin console.">
              <Input id="platform-user-email" type="email" required value={email} onChange={(event) => setEmail(event.target.value)} />
            </FormField>
            <FormField label="Display name" htmlFor="platform-user-name" tooltip="Name shown in audit and user lists.">
              <Input id="platform-user-name" required value={displayName} onChange={(event) => setDisplayName(event.target.value)} />
            </FormField>
            <FormField
              label="Access"
              htmlFor="platform-user-scope"
              tooltip="Full admin sees everything. Marketing sees only the Marketing tab and can import marketing metrics."
            >
              <Select
                id="platform-user-scope"
                value={staffScope}
                onChange={(event) => setStaffScope(event.target.value as "full" | "marketing")}
              >
                <option value="full">Full admin</option>
                <option value="marketing">Marketing only</option>
              </Select>
            </FormField>
            <FormField label="Setup link" tooltip="Email the setup link or show it here so you can share it manually.">
              <Select value={deliver} onChange={(event) => setDeliver(event.target.value as "email" | "copy")}>
                <option value="email">Email link</option>
                <option value="copy">Copy link</option>
              </Select>
            </FormField>
            <Button type="submit">Add user</Button>
          </Cluster>
          <p className="admin-muted">MFA is required for every admin user except admin@saleslox.com.</p>
        </Stack>
      </Card>
      {selected.size > 0 && (
        <Cluster gap={3} align="center">
          <span className="admin-muted">{selected.size} selected</span>
          <Button type="button" variant="secondary" disabled={busy} onClick={() => disableSelected()}>
            Disable
          </Button>
          <Button type="button" variant="danger" disabled={busy} onClick={() => setConfirmDelete(true)}>
            Delete
          </Button>
        </Cluster>
      )}
      <Card>
        <table>
          <thead>
            <tr>
              <th>
                <Checkbox
                  aria-label="Select all admin users"
                  checked={allSelected}
                  disabled={selectableUsers.length === 0}
                  onChange={toggleAll}
                />
              </th>
              <th>User</th><th>Access</th><th>Status</th><th>MFA</th><th>Last login</th><th>Created</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && (
              <tr><td colSpan={7} className="admin-muted">No platform admin users yet.</td></tr>
            )}
            {users.map((user) => {
              const selectable = user.user_id !== currentUserId && user.email.toLowerCase() !== "admin@saleslox.com";
              return (
                <tr key={user.user_id}>
                  <td>
                    <Checkbox
                      aria-label={`Select ${user.email}`}
                      checked={selected.has(user.user_id)}
                      disabled={!selectable}
                      onChange={() => toggleOne(user.user_id)}
                    />
                  </td>
                  <td>
                    {user.display_name}
                    <div className="admin-muted">{user.email}</div>
                  </td>
                  <td>{user.staff_scope === "marketing" ? "Marketing only" : "Full admin"}</td>
                  <td><StatusBadge status={user.status} /></td>
                  <td>{user.mfa_required ? `Required (${user.mfa_channel || "email"})` : "Exception"}</td>
                  <td className="admin-readonly">{formatUtc(user.last_login_at)}</td>
                  <td className="admin-readonly">{formatDateUtc(user.created_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
      <Modal
        isOpen={confirmDelete}
        onClose={() => { if (!busy) setConfirmDelete(false); }}
        title="Delete admin users"
        footer={(
          <>
            <Button type="button" variant="secondary" disabled={busy} onClick={() => setConfirmDelete(false)}>
              Cancel
            </Button>
            <Button type="button" variant="danger" disabled={busy} onClick={() => deleteSelected()}>
              Delete {selected.size} {selected.size === 1 ? "user" : "users"}
            </Button>
          </>
        )}
      >
        <p>
          This permanently removes {selected.size} admin {selected.size === 1 ? "account" : "accounts"} and revokes
          their sessions. This cannot be undone. If a user is still referenced by other records (for example a seat
          assignment they made), that deletion will fail and the user must be disabled instead.
        </p>
      </Modal>
    </Stack>
  );
}

export function CustomersPage() {
  const [orgs, setOrgs] = useState<OrgRow[]>([]);
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState<CustomerSortKey>("created");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [orgName, setOrgName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminName, setAdminName] = useState("");
  const [tier, setTier] = useState("starter");
  const [seats, setSeats] = useState("5");
  const [trial, setTrial] = useState(false);
  const [setupNotice, setSetupNotice] = useState<string | null>(null);
  const [setupLink, setSetupLink] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [billingGuard, setBillingGuard] = useState<{
    action: "disable" | "delete";
    orgs: OrgRow[];
  } | null>(null);
  const [deleteProgress, setDeleteProgress] = useState<{ done: number; total: number } | null>(null);

  async function load() {
    setError(null);
    try {
      const row = await api.get<{ organizations: OrgRow[] }>("/operator/orgs");
      setOrgs(row.organizations);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load customers.");
    }
  }

  useEffect(() => {
    void load();
  }, []);

  function toggleOne(orgId: string) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(orgId)) next.delete(orgId);
      else next.add(orgId);
      return next;
    });
  }

  function toggleAll(visibleOrgs: OrgRow[]) {
    setSelected((current) => {
      const allSelected = visibleOrgs.length > 0 && visibleOrgs.every((org) => current.has(org.org_id));
      if (allSelected) return new Set();
      return new Set(visibleOrgs.map((org) => org.org_id));
    });
  }

  async function disableSelected(skipBillingGuard = false) {
    setError(null);
    if (!skipBillingGuard) {
      const blocked = orgs.filter((org) => selected.has(org.org_id) && hasActivePaidSubscription(org));
      if (blocked.length > 0) {
        setBillingGuard({ action: "disable", orgs: blocked });
        return;
      }
    }
    setBusy(true);
    try {
      for (const orgId of selected) {
        await api.post(`/operator/orgs/${orgId}/status`, { status: "suspended" });
      }
      setSelected(new Set());
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not disable the selected customers.");
    } finally {
      setBusy(false);
    }
  }

  async function deleteSelected(skipBillingGuard = false) {
    setError(null);
    if (!skipBillingGuard) {
      const blocked = orgs.filter((org) => selected.has(org.org_id) && hasActivePaidSubscription(org));
      if (blocked.length > 0) {
        setConfirmDelete(false);
        setBillingGuard({ action: "delete", orgs: blocked });
        return;
      }
    }
    setBusy(true);
    const orgIds = Array.from(selected);
    setDeleteProgress({ done: 0, total: orgIds.length });
    const CONCURRENCY = 5;
    const failures: string[] = [];
    let done = 0;
    let cursor = 0;
    async function worker() {
      while (cursor < orgIds.length) {
        const orgId = orgIds[cursor];
        cursor += 1;
        try {
          await api.post(`/operator/orgs/${orgId}/status`, { status: "suspended" });
          await api.post(`/operator/orgs/${orgId}/purge`, {});
        } catch {
          failures.push(orgId);
        } finally {
          done += 1;
          setDeleteProgress({ done, total: orgIds.length });
        }
      }
    }
    try {
      await Promise.all(Array.from({ length: Math.min(CONCURRENCY, orgIds.length) }, () => worker()));
      setSelected(new Set(failures));
      setConfirmDelete(false);
      if (failures.length > 0) {
        setError(`Deleted ${orgIds.length - failures.length} of ${orgIds.length} customers. ${failures.length} failed and remain selected — retry when ready.`);
      }
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not delete the selected customers.");
      setConfirmDelete(false);
    } finally {
      setBusy(false);
      setDeleteProgress(null);
    }
  }

  async function cancelPaymentAndContinue() {
    if (!billingGuard) return;
    setError(null);
    setBusy(true);
    try {
      for (const org of billingGuard.orgs) {
        await api.post(`/operator/orgs/${org.org_id}/cancel-payment`, {
          comment: `Cancelled from customer ${billingGuard.action} verification.`,
        });
      }
      const action = billingGuard.action;
      setBillingGuard(null);
      await load();
      if (action === "disable") {
        await disableSelected(true);
      } else {
        await deleteSelected(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not cancel payment.");
      setBillingGuard(null);
    } finally {
      setBusy(false);
    }
  }

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const filtered = !needle
      ? orgs
      : orgs.filter((org) =>
          [
            org.display_name,
            org.slug ?? "",
            org.billing_email ?? "",
            org.org_id,
            org.status,
            orgLicenseLabel(org),
            licenseTypeLabel(orgLicenseType(org)),
            `${org.storage_mb ?? 0} MB`,
            attributionLabel(org.signup_attribution),
            (org.discount_codes ?? []).join(" "),
          ].some((value) => value.toLowerCase().includes(needle)),
        );
    return [...filtered].sort((a, b) => compareCustomers(a, b, sortKey, sortDir));
  }, [orgs, query, sortKey, sortDir]);

  function toggleSort(column: CustomerSortKey) {
    if (sortKey === column) {
      setSortDir((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(column);
    setSortDir(column === "created" || column === "people" ? "desc" : "asc");
  }

  return (
    <Stack gap={4}>
      <Cluster gap={3} align="center">
        <TipHeading
          as="h2"
          label="Customers"
          tip="All tenant organizations. Search by name, slug, billing email, or org id."
        />
        <Input value={query} onChange={(event) => setQuery(event.target.value)} aria-label="Search customers" placeholder="Search name, slug, email, id" />
        <Button type="button" variant="secondary" onClick={() => setCreating((value) => !value)}>
          {creating ? "Cancel" : "New customer"}
        </Button>
      </Cluster>
      {error && <Flash tone="error">{error}</Flash>}
      {setupNotice && (
        <p className="admin-muted">
          {setupNotice}
          {setupLink ? (
            <>
              {" "}
              <a href={setupLink}>Open setup link</a>
            </>
          ) : null}
        </p>
      )}
      {creating && (
        <Card>
          <form
            onSubmit={guardSubmit((event) => {
              event.preventDefault();
              return (async () => {
                setError(null);
                try {
                  const row = await api.post<{ org_id: string; email: string; setup_token?: string; tier: string }>(
                    "/operator/orgs",
                    {
                      org_name: orgName,
                      email: adminEmail,
                      display_name: adminName || orgName,
                      tier,
                      seats: Number(seats),
                      trial,
                    },
                  );
                  setOrgName("");
                  setAdminEmail("");
                  setAdminName("");
                  setCreating(false);
                  if (row.setup_token) {
                    setSetupLink(setupPasswordUrl(row.setup_token));
                    setSetupNotice(`Created ${row.email} on ${row.tier}. Send them the password setup link — it is not a password.`);
                  } else {
                    setSetupLink(null);
                    setSetupNotice(`Created ${row.email} on ${row.tier}.`);
                  }
                  await load();
                } catch (err) {
                  setError(err instanceof Error ? err.message : "Customer could not be created.");
                }
              })();
            })}
          >
            <Stack gap={3}>
              <TipHeading
                label="New customer"
                tip="Creates a tenant, seats, and first org admin. They get a password setup link — not a password."
              />
              <Cluster gap={2} align="end">
                <FormField
                  label="Organization"
                  htmlFor="new-org-name"
                  tooltip="Display name for the tenant. Shown in admin lists and the org console."
                >
                  <Input id="new-org-name" required value={orgName} onChange={(event) => setOrgName(event.target.value)} />
                </FormField>
                <FormField
                  label="Admin email"
                  htmlFor="new-admin-email"
                  tooltip="First org-admin login. They set a password via the setup link after create."
                >
                  <Input id="new-admin-email" required type="email" value={adminEmail} onChange={(event) => setAdminEmail(event.target.value)} />
                </FormField>
                <FormField
                  label="Admin name"
                  htmlFor="new-admin-name"
                  tooltip="Display name for the first admin. Defaults to the organization name if blank."
                >
                  <Input id="new-admin-name" value={adminName} onChange={(event) => setAdminName(event.target.value)} />
                </FormField>
                <FormField
                  label="Plan"
                  htmlFor="new-plan"
                  tooltip="Starting CRM plan. Seats snap to that plan’s default band when you change it."
                >
                  <Select id="new-plan" value={tier} onChange={(event) => {
                    const next = event.target.value;
                    setTier(next);
                    if (next === "starter") setSeats("5");
                    if (next === "professional") setSeats("11");
                    if (next === "enterprise") setSeats("21");
                  }}>
                    <option value="starter">Basic</option>
                    <option value="professional">Pro</option>
                    <option value="enterprise">Enterprise</option>
                  </Select>
                </FormField>
                <FormField
                  label="Seats"
                  tooltip="Licensed user seats for this tenant. Minimum depends on the selected plan."
                >
                  <Input type="number" min={5} value={seats} onChange={(event) => setSeats(event.target.value)} />
                </FormField>
                <Checkbox label="30-day trial (Basic, 5 users by default)" checked={trial} onChange={(event) => {
                  setTrial(event.target.checked);
                  if (event.target.checked) {
                    setTier("starter");
                    setSeats("5");
                  }
                }} />
                <Button type="submit">Create customer</Button>
              </Cluster>
              <p className="admin-muted">A paid plan grants a year of access here. A trial starts on Basic with 5 users unless you change the plan first.</p>
            </Stack>
          </form>
        </Card>
      )}
      {selected.size > 0 && (
        <Cluster gap={3} align="center">
          <span className="admin-muted">{selected.size} selected</span>
          <Button type="button" variant="secondary" disabled={busy} onClick={() => disableSelected()}>
            Disable
          </Button>
          <Button type="button" variant="danger" disabled={busy} onClick={() => setConfirmDelete(true)}>
            Delete
          </Button>
        </Cluster>
      )}
      <Card>
        <table>
          <thead>
            <tr>
              <th>
                <Checkbox
                  aria-label="Select all customers"
                  checked={visible.length > 0 && visible.every((org) => selected.has(org.org_id))}
                  disabled={visible.length === 0}
                  onChange={() => toggleAll(visible)}
                />
              </th>
              <SortableHeader label="Organization" column="organization" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortableHeader label="Slug" column="slug" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortableHeader label="Status" column="status" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortableHeader label="Created" column="created" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortableHeader label="People" column="people" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortableHeader label="Storage (MB)" column="storage" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortableHeader label="Licences" column="licences" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortableHeader label="Licence type" column="license_type" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortableHeader label="Source" column="source" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortableHeader label="Discount" column="discount" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
            </tr>
          </thead>
          <tbody>
            {visible.map((org) => {
              const licenseType = orgLicenseType(org);
              return (
                <tr key={org.org_id}>
                  <td>
                    <Checkbox
                      aria-label={`Select ${org.display_name}`}
                      checked={selected.has(org.org_id)}
                      onChange={() => toggleOne(org.org_id)}
                    />
                  </td>
                  <td>
                    <Link to={`/customers/${org.org_id}`}>{org.display_name}</Link>
                    <div className="admin-muted">{org.billing_email || org.org_id}</div>
                  </td>
                  <td><code>{org.slug}</code></td>
                  <td>
                    <CustomerStatus
                      status={org.status}
                      cancelledAt={org.cancelled_at}
                      licences={org.licenses}
                      showWhen
                    />
                  </td>
                  <td className="admin-readonly">{formatDateUtc(org.created_at)}</td>
                  <td>{org.members}</td>
                  <td>{(org.storage_mb ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                  <td>{orgLicenseLabel(org)}</td>
                  <td>
                    {licenseType ? (
                      <span className={`admin-badge is-${licenseType}`}>{licenseTypeLabel(licenseType)}</span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>{attributionLabel(org.signup_attribution)}</td>
                  <td>
                    {org.discount_codes?.length
                      ? org.discount_codes.map((code, index) => (
                          <span key={code}>{index > 0 ? ", " : ""}<code>{code}</code></span>
                        ))
                      : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {visible.length === 0 && <p className="admin-muted">No customers match.</p>}
      </Card>
      <Modal
        isOpen={confirmDelete}
        onClose={() => { if (!busy) setConfirmDelete(false); }}
        title="Delete customers"
        footer={(
          <>
            <Button type="button" variant="secondary" disabled={busy} onClick={() => setConfirmDelete(false)}>
              Cancel
            </Button>
            <Button type="button" variant="danger" disabled={busy} onClick={() => deleteSelected()}>
              Delete {selected.size} {selected.size === 1 ? "customer" : "customers"}
            </Button>
          </>
        )}
      >
        <p>
          This disables and permanently purges data for {selected.size} {selected.size === 1 ? "customer" : "customers"}
          — companies, contacts, quotes, orders, invoices, timesheets, and expenses. This cannot be undone.
        </p>
        {deleteProgress && (
          <p className="admin-muted">Deleting {deleteProgress.done} / {deleteProgress.total}…</p>
        )}
      </Modal>
      <Modal
        isOpen={Boolean(billingGuard)}
        onClose={() => { if (!busy) setBillingGuard(null); }}
        title="Active payment warning"
        footer={(
          <>
            <Button type="button" variant="secondary" disabled={busy} onClick={() => setBillingGuard(null)}>
              Cancel {billingGuard?.action ?? "action"}
            </Button>
            <Button type="button" variant="danger" disabled={busy} onClick={() => cancelPaymentAndContinue()}>
              Cancel payment and continue
            </Button>
          </>
        )}
      >
        <Stack gap={2}>
          <p>
            {billingGuard?.orgs.length ?? 0} selected {billingGuard?.orgs.length === 1 ? "customer has" : "customers have"}
            {" "}an active paid subscription. A customer cannot be disabled or deleted while payment is active.
          </p>
          <ul>
            {billingGuard?.orgs.map((org) => (
              <li key={org.org_id}>{org.display_name}</li>
            ))}
          </ul>
        </Stack>
      </Modal>
    </Stack>
  );
}

export function CustomerDetailPage() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const [detail, setDetail] = useState<OrgDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tier, setTier] = useState("starter");
  const [seats, setSeats] = useState("5");
  const [billDate, setBillDate] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [newAdmin, setNewAdmin] = useState(false);
  const [setupNotice, setSetupNotice] = useState<string | null>(null);
  const [setupLink, setSetupLink] = useState<string | null>(null);
  const [setupUser, setSetupUser] = useState<MemberRow | null>(null);
  const [setupBusy, setSetupBusy] = useState<"email" | "copy" | null>(null);
  const [reassign, setReassign] = useState<ReassignPrompt | null>(null);
  const [reassignBusy, setReassignBusy] = useState(false);
  const [clearingLocksFor, setClearingLocksFor] = useState<string | null>(null);
  const [deleteMember, setDeleteMember] = useState<MemberRow | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [locksNotice, setLocksNotice] = useState<string | null>(null);
  const [slug, setSlug] = useState("");
  const [extendDays, setExtendDays] = useState("30");
  const [billingMode, setBillingMode] = useState<"month" | "year">("month");
  const [partner, setPartner] = useState(false);
  const [nurtureDays, setNurtureDays] = useState<Record<string, string>>({});
  const [emailLog, setEmailLog] = useState<EmailLogRow[] | null>(null);
  const [emailLogOpen, setEmailLogOpen] = useState(false);
  const [emailLogExpanded, setEmailLogExpanded] = useState<string | null>(null);
  const [billingJsonView, setBillingJsonView] = useState<{ title: string; value: unknown } | null>(null);
  const [billingGuardAction, setBillingGuardAction] = useState<"disable" | "delete" | null>(null);

  async function load() {
    if (!orgId) return;
    setError(null);
    try {
      const row = await api.get<OrgDetail>(`/operator/orgs/${orgId}`);
      setDetail(row);
      setSlug(row.slug ?? "");
      const current = row.current_plan ?? row.licenses.find((item) => item.app_code === "crm");
      if (current?.tier) setTier(current.tier);
      if (current?.seats) setSeats(String(current.seats));
      setPartner(current?.grant_source === "billing_exempt");
      if (current?.grant_source === "billing_exempt") {
        setExtendDays("180");
      } else if (current?.billing_interval === "year") {
        setBillingMode("year");
      } else {
        setBillingMode("month");
      }
      const due = current?.billing_due_at || row.next_bill?.renewal_at || current?.access_ends_at;
      setBillDate(due ? due.slice(0, 10) : "");
      const days = await api.get<Record<string, number>>("/operator/trial-nurture/days");
      setNurtureDays({
        day_10: String(days.day_10),
        day_20: String(days.day_20),
        day_25: String(days.day_25),
        day_28: String(days.day_28),
        day_30: String(days.day_30),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Customer could not be loaded.");
    }
  }

  async function loadEmailLog() {
    if (!orgId) return;
    try {
      const result = await api.get<{ emails: EmailLogRow[] }>(`/operator/orgs/${orgId}/email-log`);
      setEmailLog(result.emails);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Email log could not be loaded.");
    }
  }

  function toggleEmailLog() {
    const next = !emailLogOpen;
    setEmailLogOpen(next);
    if (next && emailLog === null) {
      void loadEmailLog();
    }
  }

  async function saveNurtureDays() {
    const payload: Record<string, number> = {};
    for (const step of ["day_10", "day_20", "day_25", "day_28", "day_30"]) {
      const parsed = Number(nurtureDays[step]);
      if (!Number.isInteger(parsed) || parsed < 1 || parsed > 365) {
        setError("Days must be a whole number between 1 and 365.");
        return;
      }
      payload[step] = parsed;
    }
    setError(null);
    try {
      const days = await api.patch<Record<string, number>>("/operator/trial-nurture/days", payload);
      setNurtureDays({
        day_10: String(days.day_10),
        day_20: String(days.day_20),
        day_25: String(days.day_25),
        day_28: String(days.day_28),
        day_30: String(days.day_30),
      });
      notifySaved("Trial email days saved.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Trial sequence days could not be saved.");
    }
  }

  useEffect(() => {
    void load();
  }, [orgId]);

  async function run(action: () => Promise<unknown>) {
    setError(null);
    try {
      await action();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Update failed.");
    }
  }

  async function changeMembership(member: MemberRow, disabled: boolean) {
    if (!detail || !member.user_id) return;
    if (disabled) {
      await run(() =>
        api.post(`/operator/orgs/${detail.org_id}/members/${member.user_id}/status`, {
          membership_status: "active",
        }),
      );
      return;
    }
    setError(null);
    try {
      await api.post(`/operator/orgs/${detail.org_id}/members/${member.user_id}/status`, {
        membership_status: "revoked",
      });
      await load();
    } catch (err) {
      const pending = err instanceof ApiError ? readReassign(err) : null;
      if (pending) {
        setReassign({
          userId: member.user_id,
          name: member.display_name || member.email,
          items: pending.items,
          managers: pending.sales_managers,
          managerId: pending.sales_managers[0]?.user_id ?? "",
        });
        return;
      }
      setError(err instanceof Error ? err.message : "Update failed.");
    }
  }

  async function confirmDeleteMember() {
    if (!detail || !deleteMember?.user_id) return;
    setDeleteBusy(true);
    await run(() => api.delete(`/operator/orgs/${detail.org_id}/members/${deleteMember.user_id}`));
    setDeleteBusy(false);
    setDeleteMember(null);
  }

  async function confirmReassign() {
    if (!detail || !reassign?.managerId) return;
    setError(null);
    setReassignBusy(true);
    try {
      await api.post(`/operator/orgs/${detail.org_id}/members/${reassign.userId}/status`, {
        membership_status: "revoked",
        reassign_to_user_id: reassign.managerId,
      });
      setReassign(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Open work could not be reassigned.");
    } finally {
      setReassignBusy(false);
    }
  }

  async function guardedCustomerAction(action: "disable" | "enable" | "delete", skipBillingGuard = false) {
    if (!detail) return;
    if (!skipBillingGuard && action !== "enable" && hasActivePaidSubscription(detail)) {
      setBillingGuardAction(action);
      return;
    }
    if (action === "delete") {
      await run(() => api.post(`/operator/orgs/${detail.org_id}/purge`, {}));
      return;
    }
    await run(() =>
      api.post(`/operator/orgs/${detail.org_id}/status`, {
        status: action === "disable" ? "suspended" : "active",
      }),
    );
  }

  async function cancelCustomerPaymentAndContinue() {
    if (!detail || !billingGuardAction) return;
    const action = billingGuardAction;
    setError(null);
    try {
      await api.post(`/operator/orgs/${detail.org_id}/cancel-payment`, {
        comment: `Cancelled from customer ${action} verification.`,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not cancel payment.");
      return;
    }
    setBillingGuardAction(null);
    await load();
    await guardedCustomerAction(action, true);
  }

  async function deliverSetup(user: MemberRow, deliver: "email" | "copy") {
    if (!orgId) return;
    setError(null);
    setSetupBusy(deliver);
    try {
      const result = await api.post<SetupLinkResult>(
        `/operator/orgs/${orgId}/members/${encodeURIComponent(user.user_id)}/setup-link`,
        { deliver },
      );
      const url = setupPasswordUrl(result.setup_token);
      setSetupLink(url);
      if (deliver === "copy") {
        try {
          await navigator.clipboard.writeText(url);
          setSetupNotice("Copied. Send this link to the person — it expires in 24 hours and is not a password.");
        } catch {
          setSetupNotice("Copy this once and send it to the person. It expires in 24 hours and is not a password.");
        }
      } else if (result.emailed) {
        setSetupNotice(`Sent to ${result.email}. They have 24 hours to set a password.`);
      } else if (result.delivery === "not_configured") {
        await navigator.clipboard.writeText(url).catch(() => undefined);
        setSetupNotice("Email isn't configured here. The link is copied — send it to the person yourself.");
      } else {
        await navigator.clipboard.writeText(url).catch(() => undefined);
        setSetupNotice("The email couldn't be sent. The link is copied — send it to the person yourself.");
      }
      setSetupUser(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Setup link could not be created.");
    } finally {
      setSetupBusy(null);
    }
  }

  async function clearLoginLocks(user: MemberRow) {
    if (!orgId) return;
    setError(null);
    setClearingLocksFor(user.user_id);
    try {
      const result = await api.post<{ email: string; status: string }>(
        `/operator/orgs/${orgId}/members/${encodeURIComponent(user.user_id)}/clear-login-locks`,
      );
      setLocksNotice(`Cleared login and reset-request locks for ${result.email}. They can try again now.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Locks could not be cleared.");
    } finally {
      setClearingLocksFor(null);
    }
  }

  if (!detail) {
    return (
      <Stack gap={3}>
        <Button variant="ghost" onClick={() => navigate("/customers")}>Back</Button>
        {error ? <Flash tone="error">{error}</Flash> : <p>Loading…</p>}
      </Stack>
    );
  }

  const crmPlans = crmPlanOptions(detail.plans);
  const currentPlan = detail.current_plan ?? detail.licenses.find((item) => item.app_code === "crm") ?? null;
  const nextStatus = detail.status === "active" ? "suspended" : "active";
  const history = detail.plan_history ?? [];
  const codesUsed = usedDiscountCodes(detail);
  const selectedPlan = crmPlans.find((plan) => plan.tier === tier) ?? crmPlans[0];
  const seatMin = selectedPlan.min_seats;
  const seatMax = selectedPlan.max_seats ?? undefined;
  const isTrial = currentPlan?.grant_source === "trial";
  const isExempt = currentPlan?.grant_source === "billing_exempt";
  const billingLabel = isExempt
    ? "Billing exempt"
    : billingFrequencyLabel(
        currentPlan?.billing_interval ?? detail.next_bill?.interval ?? (isTrial ? "month" : currentPlan ? "year" : null),
      );
  const endsLabel = currentPlan?.access_ends_at
    ? new Date(currentPlan.access_ends_at).toLocaleDateString()
    : "no end date";

  return (
    <Stack gap={5}>
      <Cluster gap={3} align="center">
        <Button variant="ghost" onClick={() => navigate("/customers")}>Back</Button>
        <h2>{detail.display_name}</h2>
        <CustomerStatus
          status={detail.status}
          cancelledAt={detail.cancelled_at}
          licences={detail.licenses}
          currentPlan={currentPlan}
        />
      </Cluster>
      {error && <Flash tone="error">{error}</Flash>}
      <Card>
        <Stack gap={3}>
          <div className="admin-tenant-head">
            <Cluster gap={3} align="center">
              <TipHeading
                label="Tenant"
                tip="Identity and signup context for this organization: status, plan flags, slug, and welcome mail."
              />
              <p className="admin-since">Customer since {formatCustomerSince(detail.customer_since)}</p>
            </Cluster>
            <Cluster gap={3} align="center" className="admin-tenant-flags">
              <CustomerStatus
                status={detail.status}
                cancelledAt={detail.cancelled_at}
                licences={detail.licenses}
                currentPlan={currentPlan}
                className="admin-flag"
              />
              {isTrial ? <span className="admin-badge is-trial admin-flag">Trial</span> : null}
              {currentPlan?.tier ? (
                <span className={`admin-badge is-plan-${currentPlan.tier} admin-flag`}>{planName(currentPlan.tier)}</span>
              ) : null}
              {billingLabel ? (
                <span className={`admin-badge ${isExempt ? "is-exempt" : "is-billing"} admin-flag`}>{billingLabel}</span>
              ) : null}
            </Cluster>
          </div>
          <p className="admin-muted">{detail.org_id} · {detail.billing_email || "No billing email"}</p>
          <p className="admin-muted">Signed up from {attributionSummary(detail.signup_attribution)}</p>
          <p className="admin-muted">
            Discount codes used:{" "}
            {codesUsed.length
              ? codesUsed.map((code, index) => (
                  <span key={code}>{index > 0 ? ", " : ""}<code>{code}</code></span>
                ))
              : "none"}
          </p>
          {detail.website_visit ? (
            <Stack gap={2}>
              <p className="admin-muted">
                Pages before signup · {formatDurationMs(detail.website_visit.total_ms)}
                {detail.website_visit.source_ip ? ` · ${detail.website_visit.source_ip}` : ""}
                {detail.website_visit.country ? ` · ${detail.website_visit.country}` : ""}
              </p>
              <div className="admin-journey">
                {detail.website_visit.pages.map((page, index) => (
                  <span key={`${page.path}-${index}`} className="admin-journey-step">
                    <code>{page.path}</code>
                    <span className="admin-muted"> {formatDurationMs(page.duration_ms)}</span>
                  </span>
                ))}
              </div>
            </Stack>
          ) : null}
          <p className="admin-muted">Customer workspace: <code>/apps/crm/{detail.slug}/companies</code></p>
          <form
            onSubmit={guardSubmit((event) => {
              event.preventDefault();
              return run(() => api.patch(`/operator/orgs/${detail.org_id}/slug`, { slug }));
            })}
          >
            <Stack gap={3}>
              <FormField
                label="Customer slug"
                tooltip="Unique URL segment for this tenant’s org console path. Lowercase letters, numbers, and hyphens only."
              >
                <Input
                  required
                  minLength={2}
                  maxLength={80}
                  pattern="[a-z0-9]+(-[a-z0-9]+)*"
                  value={slug}
                  onChange={(event) => setSlug(event.target.value)}
                  aria-label="Customer slug"
                />
              </FormField>
              <p className="admin-muted">Lowercase letters, numbers, and hyphens. This is the unique tenant segment in the org console URL.</p>
              <Cluster gap={2}>
                <Button type="submit" disabled={slug === detail.slug}>Save slug</Button>
              </Cluster>
            </Stack>
          </form>
          <Stack gap={2}>
            <Checkbox
              label="Welcome email sent"
              checked={Boolean(detail.welcome_email_sent)}
              onChange={(event) => {
                const sent = event.target.checked;
                void run(() =>
                  api.patch(`/operator/orgs/${detail.org_id}/welcome-email`, {
                    welcome_email_sent: sent,
                  }),
                );
              }}
            />
            {detail.welcome_email_sent ? (
              <p className="admin-muted">
                {detail.welcome_email_sent_at
                  ? `Sent ${formatUtc(detail.welcome_email_sent_at)}. Uncheck to allow sending again.`
                  : "Uncheck to allow sending again."}
              </p>
            ) : (
              <p className="admin-muted">
                Not sent yet. Trial signup sends the Email-page Welcome template automatically
                when SMTP succeeds. Paid signup sends Paid signup (with receipt). A later
                conversion sends Conversion. PayFast checkout waits until payment. Use Send
                welcome email to retry the signup mail.
              </p>
            )}
            {detail.welcome_email_sent ? null : (
              <Cluster gap={2}>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => run(() => api.post(`/operator/orgs/${detail.org_id}/welcome-email`, {}))}
                >
                  Send welcome email
                </Button>
              </Cluster>
            )}
            {detail.trial_email_plan ? <TrialEmailPlan plan={detail.trial_email_plan} /> : null}
            <p className="admin-muted">
              Trial sequence — uncheck a box to allow that letter again. Purchase checks every box. The day is
              counted from trial start, is platform-wide (applies to every trial), and is editable below.
            </p>
            {(
              [
                ["day_10", "Trial Mail 01"],
                ["day_20", "Trial Mail 02"],
                ["day_25", "Trial Mail 03"],
                ["day_28", "Trial Mail 04"],
                ["day_30", "Trial Mail 05"],
              ] as const
            ).map(([step, label]) => (
              <Stack key={step} gap={1}>
                <Checkbox
                  label={nurtureStepLabel(label, detail.trial_nurture?.[step], detail.trial_nurture_sources?.[step])}
                  checked={Boolean(detail.trial_nurture?.[step])}
                  onChange={(event) => {
                    const sent = event.target.checked;
                    void run(() =>
                      api.patch(`/operator/orgs/${detail.org_id}/trial-nurture`, {
                        step_key: step,
                        sent,
                      }),
                    );
                  }}
                />
                <Cluster gap={2} align="center">
                  <span className="admin-muted">Sent email on day:</span>
                  <Input
                    type="number"
                    min={1}
                    max={365}
                    aria-label={`${label} day`}
                    value={nurtureDays[step] ?? ""}
                    onChange={(event) => setNurtureDays((current) => ({ ...current, [step]: event.target.value }))}
                  />
                </Cluster>
              </Stack>
            ))}
            <Cluster gap={2}>
              <Button type="button" onClick={() => void saveNurtureDays()}>
                Save trial email days
              </Button>
            </Cluster>
          </Stack>
          <Stack gap={2}>
            <Button type="button" variant="ghost" onClick={toggleEmailLog}>
              {emailLogOpen ? "Hide email log" : "Show email log"}
            </Button>
            {emailLogOpen && (
              emailLog === null ? (
                <p className="admin-muted">Loading…</p>
              ) : emailLog.length === 0 ? (
                <p className="admin-muted">No emails logged yet for this customer.</p>
              ) : (
                <Stack gap={2}>
                  {emailLog.map((entry, index) => {
                    const rowKey = `${entry.template_key}-${entry.sent_at}-${index}`;
                    const expanded = emailLogExpanded === rowKey;
                    return (
                      <Card key={rowKey}>
                        <Stack gap={1}>
                          <Cluster gap={3} align="center">
                            <strong>{entry.subject}</strong>
                          </Cluster>
                          <p className="admin-muted">
                            To {entry.to_email} · {entry.sent_at ? formatUtc(entry.sent_at) : "unknown time"} ·{" "}
                            {entry.template_key}
                          </p>
                          <Button
                            type="button"
                            variant="ghost"
                            onClick={() => setEmailLogExpanded(expanded ? null : rowKey)}
                          >
                            {expanded ? "Hide email body" : "Show email body"}
                          </Button>
                          {expanded && (
                            <iframe
                              title={`email-body-${rowKey}`}
                              className="admin-email-log-frame"
                              srcDoc={entry.html_body}
                              sandbox=""
                            />
                          )}
                        </Stack>
                      </Card>
                    );
                  })}
                </Stack>
              )
            )}
          </Stack>
          <Cluster gap={2}>
            <Button
              variant={nextStatus === "suspended" ? "danger" : "secondary"}
              onClick={() => guardedCustomerAction(nextStatus === "suspended" ? "disable" : "enable")}
            >
              {nextStatus === "suspended" ? "Disable customer" : "Enable customer"}
            </Button>
            <Button
              variant="ghost"
              disabled={detail.status === "active"}
              title={detail.status === "active" ? "Disable the customer before purging." : undefined}
              onClick={() => guardedCustomerAction("delete")}
            >
              Purge
            </Button>
          </Cluster>
          {detail.status === "active" && (
            <p className="admin-muted">Disable this customer before purging its data.</p>
          )}
          {detail.purged_at && <p className="admin-muted">Purged {detail.purged_at}.</p>}
        </Stack>
      </Card>
      <Modal
        isOpen={Boolean(billingGuardAction)}
        onClose={() => setBillingGuardAction(null)}
        title="Active payment warning"
        footer={(
          <>
            <Button type="button" variant="secondary" onClick={() => setBillingGuardAction(null)}>
              Cancel {billingGuardAction ?? "action"}
            </Button>
            <Button type="button" variant="danger" onClick={() => cancelCustomerPaymentAndContinue()}>
              Cancel payment and continue
            </Button>
          </>
        )}
      >
        <p>
          {detail.display_name} has an active paid subscription. A customer cannot be disabled or deleted while payment is active.
        </p>
      </Modal>
      {detail.usage ? <CustomerUsageCard usage={detail.usage} /> : null}
      <Card>
        <Stack gap={3}>
          <TipHeading
            label="People"
            tip="Org-console users for this tenant. Disable revokes membership and ends their active session."
          />
          <p className="admin-muted">Changes here update the organization console immediately. Disabled people lose their active session. If they still own open sales work, choose a sales manager to take it. Closed records stay with them. Someone who has never logged on and owns nothing can be deleted. Last logged on is read-only and shown in UTC.</p>
          {setupNotice && (
            <Stack gap={2}>
              <strong>Password setup link</strong>
              <p className="admin-muted">{setupNotice}</p>
              {setupLink ? <code className="admin-setup-link">{setupLink}</code> : null}
            </Stack>
          )}
          {locksNotice && (
            <Stack gap={2}>
              <strong>Login locks</strong>
              <p className="admin-muted">{locksNotice}</p>
            </Stack>
          )}
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Role</th>
                <th>Last logged on (UTC)</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {detail.members.map((member) => {
                const disabled = (member.membership_status ?? member.status) === "revoked";
                return (
                  <tr key={member.user_id || member.email}>
                    <td>{member.email}</td>
                    <td>{member.display_name}</td>
                    <td>{memberRoleLabel(member)}</td>
                    <td className="admin-readonly">{formatUtc(member.last_login_at)}</td>
                    <td>{disabled ? "Disabled" : member.status}</td>
                    <td>
                      <Cluster gap={2}>
                        <Button
                          type="button"
                          variant={disabled ? "secondary" : "danger"}
                          size="sm"
                          onClick={() => void changeMembership(member, disabled)}
                        >
                          {disabled ? "Enable" : "Disable"}
                        </Button>
                        <Button type="button" variant="secondary" size="sm" onClick={() => setSetupUser(member)}>
                          Setup link
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          disabled={clearingLocksFor === member.user_id}
                          onClick={() => clearLoginLocks(member)}
                        >
                          {clearingLocksFor === member.user_id ? "Clearing…" : "Clear login locks"}
                        </Button>
                        {!member.last_login_at && member.user_id ? (
                          <Button type="button" variant="danger" size="sm" onClick={() => setDeleteMember(member)}>
                            Delete
                          </Button>
                        ) : null}
                      </Cluster>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Cluster gap={2} align="end">
            <FormField
              label="Email"
              tooltip="Login email for the new member. They receive a password setup link after you add them."
            >
              <Input value={newEmail} onChange={(event) => setNewEmail(event.target.value)} />
            </FormField>
            <FormField
              label="Name"
              tooltip="Display name shown in the org console and admin lists."
            >
              <Input value={newName} onChange={(event) => setNewName(event.target.value)} />
            </FormField>
            <Checkbox label="Org admin" checked={newAdmin} onChange={(event) => setNewAdmin(event.target.checked)} />
            <Button
              onClick={() =>
                void run(async () => {
                  const row = await api.post<OrgDetail>(`/operator/orgs/${detail.org_id}/members`, {
                    email: newEmail,
                    display_name: newName,
                    is_org_admin: newAdmin,
                  });
                  setNewEmail("");
                  setNewName("");
                  setNewAdmin(false);
                  if (row.created_member?.setup_token) {
                    setSetupNotice(`Send ${row.created_member.email} the password setup link — it is not a password.`);
                    setSetupLink(setupPasswordUrl(row.created_member.setup_token));
                  } else {
                    setSetupNotice(`${row.created_member?.email ?? newEmail} was added and is visible in the org console.`);
                    setSetupLink(null);
                  }
                })
              }
            >
              Add user
            </Button>
          </Cluster>
          <Modal
            isOpen={setupUser !== null}
            onClose={() => { if (!setupBusy) setSetupUser(null); }}
            title="Password setup link"
            footer={(
              <>
                <Button type="button" variant="secondary" disabled={Boolean(setupBusy)} onClick={() => setSetupUser(null)}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={Boolean(setupBusy)}
                  onClick={() => (setupUser ? deliverSetup(setupUser, "copy") : undefined)}
                >
                  {setupBusy === "copy" ? "Copying…" : "Copy link"}
                </Button>
                <Button
                  type="button"
                  disabled={Boolean(setupBusy)}
                  onClick={() => (setupUser ? deliverSetup(setupUser, "email") : undefined)}
                >
                  {setupBusy === "email" ? "Sending…" : `Email ${setupUser?.email ?? ""}`}
                </Button>
              </>
            )}
          >
            <p className="admin-muted">
              Send a one-time password setup link to {setupUser?.email}, or copy it to share yourself. The link expires in 24 hours.
            </p>
          </Modal>
          <Modal
            isOpen={reassign !== null}
            onClose={() => { if (!reassignBusy) setReassign(null); }}
            title="Reassign open work"
            footer={(
              <>
                <Button type="button" variant="secondary" disabled={reassignBusy} onClick={() => setReassign(null)}>
                  Cancel
                </Button>
                <Button type="button" disabled={reassignBusy || !reassign?.managerId} onClick={() => void confirmReassign()}>
                  {reassignBusy ? "Saving…" : "Disable and reassign"}
                </Button>
              </>
            )}
          >
            <Stack gap={3}>
              <p className="admin-muted">
                {reassign?.name} still owns open sales work. Choose a sales manager to take it so those deals keep moving.
                Closed quotes, orders, invoices, and won or lost deals stay with {reassign?.name} as history.
              </p>
              <Stack gap={1}>
                {reassign?.items.map((item) => (
                  <p key={item.key}>{item.label}: {item.count}</p>
                ))}
              </Stack>
              {reassign && reassign.managers.length === 0 ? (
                <p className="admin-muted">
                  There is no other active sales manager. Give someone the Sales manager role in the organization, then disable this person.
                </p>
              ) : (
                <Select
                  label="Sales manager"
                  value={reassign?.managerId ?? ""}
                  onChange={(event) => {
                    const managerId = event.target.value;
                    setReassign((current) => (current ? { ...current, managerId } : current));
                  }}
                >
                  {(reassign?.managers ?? []).map((manager) => (
                    <option key={manager.user_id} value={manager.user_id}>
                      {manager.display_name} ({manager.email})
                    </option>
                  ))}
                </Select>
              )}
            </Stack>
          </Modal>
          <Modal
            isOpen={deleteMember !== null}
            onClose={() => { if (!deleteBusy) setDeleteMember(null); }}
            title="Delete person"
            footer={(
              <>
                <Button type="button" variant="secondary" disabled={deleteBusy} onClick={() => setDeleteMember(null)}>
                  Cancel
                </Button>
                <Button type="button" variant="danger" disabled={deleteBusy} onClick={() => void confirmDeleteMember()}>
                  {deleteBusy ? "Deleting…" : `Delete ${deleteMember?.email ?? ""}`}
                </Button>
              </>
            )}
          >
            <p className="admin-muted">
              {deleteMember?.email} has never logged on. Deleting removes them from {detail.display_name} permanently.
              This cannot be undone. If any record still names them as owner, the delete is refused — disable them instead.
            </p>
          </Modal>
        </Stack>
      </Card>
      <Card>
        <Stack gap={3}>
          <Cluster gap={4} align="start" justify="between" className="admin-bill-dates">
            <Stack gap={2}>
              <TipHeading
                label="Licence"
                tip="Active CRM plan, billing date, and access window. One plan is active at a time."
              />
              <p className="admin-muted">One CRM plan is active at a time. Upgrades and downgrades are recorded below. Disabled customers cancel billing immediately. Partners and resellers are marked with the Partner licence checkbox: they stay active, are not on a trial, and are not invoiced. Their access still expires (180 days unless you extend it).</p>
            </Stack>
            <Cluster gap={2} align="end" justify="center" className="admin-bill-picker">
              <FormField
                label="Next billing date"
                tooltip="When the next invoice is due. Disabled for billing-exempt or inactive customers."
              >
                <Input
                  type="date"
                  value={billDate}
                  onChange={(event) => setBillDate(event.target.value)}
                  aria-label="Next billing date"
                />
              </FormField>
              <Button
                onClick={() =>
                  void run(() =>
                    api.post(`/operator/orgs/${detail.org_id}/billing-date`, { billed_at: billDate }),
                  )
                }
                disabled={!billDate || !currentPlan || detail.status !== "active" || isExempt}
              >
                Save date
              </Button>
            </Cluster>
            {detail.next_bill ? (
              <NextBillNote
                amount={detail.next_bill.amount}
                currency={detail.next_bill.currency}
                billedAt={detail.next_bill.billed_at}
                renewalAt={detail.next_bill.renewal_at}
              />
            ) : (
              <p className="admin-muted">No next bill — this customer is not being invoiced.</p>
            )}
          </Cluster>
          {currentPlan ? (
            <p>
              <strong>{currentPlan.tier}</strong> · {currentPlan.seats} seats · {currentPlan.status}
              {currentPlan.grant_source ? ` · ${currentPlan.grant_source}` : ""}
              {" · "}
              <span className="admin-date-mark">
                {currentPlan.access_ends_at ? `ends ${endsLabel}` : "no end date"}
              </span>
            </p>
          ) : (
            <p className="admin-muted">No active CRM plan.</p>
          )}
          <Cluster gap={2} align="end">
            <FormField
              label="Extend (days)"
              tooltip="Adds this many days to the current CRM access end date without changing the plan."
            >
              <Input value={extendDays} onChange={(event) => setExtendDays(event.target.value)} />
            </FormField>
            <Button
              onClick={() =>
                void run(() =>
                  api.post(`/operator/orgs/${detail.org_id}/extend`, {
                    app_code: "crm",
                    days: Number(extendDays),
                  }),
                )
              }
            >
              Extend access
            </Button>
          </Cluster>
          <Cluster gap={2} align="end">
            <FormField
              label="Plan"
              htmlFor="apply-plan"
              tooltip="CRM plan to apply. Seat limits follow that plan’s band."
            >
              <Select
                id="apply-plan"
                aria-label="Plan"
                value={tier}
                onChange={(event) => {
                  const next = event.target.value;
                  setTier(next);
                  const plan = crmPlans.find((row) => row.tier === next) ?? crmPlans[0];
                  setSeats(seatsForPlan(plan, Number(seats)));
                }}
              >
                {crmPlans.map((plan) => (
                  <option key={plan.tier} value={plan.tier}>
                    {plan.display_name} ({plan.min_seats}
                    {plan.max_seats ? `–${plan.max_seats}` : "+"} users)
                  </option>
                ))}
              </Select>
            </FormField>
            <FormField
              label="Seats"
              htmlFor="apply-seats"
              tooltip="Licensed seats for the selected plan. Must stay within that plan’s min and max."
            >
              <Input
                id="apply-seats"
                aria-label="Seats"
                type="number"
                min={seatMin}
                max={seatMax}
                value={seats}
                onChange={(event) => setSeats(event.target.value)}
              />
            </FormField>
            <FormField
              label="Billing"
              htmlFor="apply-billing"
              tooltip="Month or year invoicing. Not used for partner licences, which are never invoiced."
            >
              <Select
                id="apply-billing"
                aria-label="Billing"
                value={billingMode}
                disabled={partner}
                onChange={(event) => setBillingMode(event.target.value as "month" | "year")}
              >
                <option value="month">Monthly</option>
                <option value="year">Yearly</option>
              </Select>
            </FormField>
            <Checkbox
              id="apply-partner"
              label="Partner licence"
              tooltip="For resellers who sell on our behalf. Stays active without being invoiced, is not a trial, and still expires on its access end date."
              checked={partner}
              onChange={(event) => setPartner(event.target.checked)}
            />
            <Button
              onClick={() =>
                void run(() =>
                  api.post(`/operator/orgs/${detail.org_id}/licenses`, {
                    app_code: "crm",
                    tier,
                    seats: Number(seats),
                    ...(partner
                      ? { billing_exempt: true, ...(isExempt ? {} : { days: 180 }) }
                      : isTrial
                        ? { days: 365 }
                        : {}),
                  }),
                )
              }
            >
              Apply plan
            </Button>
          </Cluster>
          <p className="admin-muted">
            Apply Basic, Pro, or Enterprise to this customer. After a Pro trial signup, choose Enterprise here so they can test those features. Applying a paid plan ends the trial and grants a year of operator access. A Partner licence keeps them active without a billable cycle and grants 180 days of partner access (it still expires). Seats snap to the plan band: Basic 5–10, Pro 11–20, Enterprise 21+.
          </p>
          <Card>
            <TipKicker label="Payment method" tip="Masked card brand and last four from PayFast. The recurring-billing token itself is never sent to any client -- it stays server-side and is only decrypted immediately before a PayFast API call." />
            <Stack gap={1}>
              <span className="admin-muted">
                {detail.payment_method?.last4
                  ? `${detail.payment_method.brand || "card"} ending ${detail.payment_method.last4}`
                  : "No card on file"}
              </span>
            </Stack>
          </Card>
          <h4 className="admin-history-title">Plan history</h4>
          <table>
            <thead>
              <tr><th>When</th><th>Change</th><th>PayFast transaction ID</th><th>Discount</th><th>Audit JSON</th><th>Who</th></tr>
            </thead>
            <tbody>
              {history.length === 0 && (
                <tr><td colSpan={6} className="admin-muted">No plan changes recorded yet.</td></tr>
              )}
              {history.map((row, index) => (
                <tr key={`${row.kind}-${row.created_at}-${index}`}>
                  <td>{row.created_at ? formatCustomerSince(row.created_at) : "—"}</td>
                  <td>{historyChange(row)}</td>
                  <td>
                    <Stack gap={1}>
                      <code>{historyPaymentId(row) || "-"}</code>
                      {row.payfast_subscription_payment_id ? (
                        <span className="admin-muted">Sub {row.payfast_subscription_payment_id}</span>
                      ) : null}
                      {row.refunded_payment_event_id ? <span className="admin-muted">Refund of {row.refunded_payment_event_id}</span> : null}
                    </Stack>
                  </td>
                  <td>{historyDiscount(row)}</td>
                  <td>
                    <Cluster gap={1}>
                      <Button
                        type="button"
                        variant="ghost"
                        disabled={!row.submitted_request}
                        onClick={() => setBillingJsonView({ title: "Submitted request", value: row.submitted_request })}
                      >
                        View submitted request
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        disabled={!row.received_response}
                        onClick={() => setBillingJsonView({ title: "Received response", value: row.received_response })}
                      >
                        View received response
                      </Button>
                    </Cluster>
                  </td>
                  <td>{row.actor_name || row.actor_email || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Stack>
      </Card>
      <OrgAiBudgetCard orgId={detail.org_id} />
      <LeadGenCard orgId={detail.org_id} />
      <LoxleyCard orgId={detail.org_id} />
      <MailboxAiCard orgId={detail.org_id} />
      <AiBillingCard orgId={detail.org_id} />
      <AllowanceCalculator orgId={detail.org_id} />
      {billingJsonView ? (
        <Modal isOpen={Boolean(billingJsonView)} title={billingJsonView.title} onClose={() => setBillingJsonView(null)} width={760}>
          <pre className="admin-json-view">{JSON.stringify(billingJsonView.value ?? {}, null, 2)}</pre>
        </Modal>
      ) : null}
    </Stack>
  );
}

const USAGE_RECORD_LABELS: Array<{ key: string; label: string }> = [
  { key: "companies", label: "Companies" },
  { key: "contacts", label: "Contacts" },
  { key: "leads", label: "Leads" },
  { key: "opportunities", label: "Opportunities" },
  { key: "quotes", label: "Quotes" },
  { key: "orders", label: "Orders" },
  { key: "invoices", label: "Invoices" },
  { key: "activities", label: "Activities" },
  { key: "products", label: "Products" },
  { key: "timesheet_entries", label: "Timesheet entries" },
  { key: "expense_reports", label: "Expense reports" },
];

const USAGE_STORAGE_LABELS: Array<{ key: string; label: string }> = [
  { key: "crm", label: "CRM data" },
  { key: "workforce", label: "Timesheet & expense" },
  { key: "branding", label: "Branding / logo" },
];

interface LeadGenSettings {
  enabled: boolean;
  switched_on: boolean;
  expires_at: string | null;
  expired: boolean;
  monthly_limit: number;
  used_this_month: number;
  updated_at: string | null;
  ai_this_month?: {
    runs: number;
    byok_runs: number;
    ai_calls: number;
    ai_tokens: number;
    ai_cost_usd: number;
    ai_cost_per_lead_usd: number | null;
  };
}

function leadGenAiLine(ai: NonNullable<LeadGenSettings["ai_this_month"]>): string {
  if (!ai.runs) return "No searches this month.";
  const runs = `${ai.runs} ${ai.runs === 1 ? "search" : "searches"}`;
  const cost = `${formatAiUsd(ai.ai_cost_usd)} AI cost (${ai.ai_tokens.toLocaleString("en-US")} tokens, ${ai.ai_calls} calls)`;
  const perLead = ai.ai_cost_per_lead_usd === null ? "" : `, ${formatAiUsd(ai.ai_cost_per_lead_usd)} per lead`;
  const byok = ai.byok_runs ? ` ${ai.byok_runs} used the customer's own AI key, which is not costed here.` : "";
  return `${runs}: ${cost}${perLead}.${byok}`;
}

/** Per-customer Lead Generator add-on: outside every plan, switched on here by staff. */
function LeadGenCard({ orgId }: { orgId: string }) {
  const [row, setRow] = useState<LeadGenSettings | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [limit, setLimit] = useState("20");
  const [expires, setExpires] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  function apply(next: LeadGenSettings) {
    setRow(next);
    setEnabled(Boolean(next?.switched_on ?? next?.enabled));
    setLimit(String(next.monthly_limit));
    setExpires(expiryInputValue(next?.expires_at));
  }

  useEffect(() => {
    api
      .get<LeadGenSettings>(`/operator/orgs/${orgId}/lead-generator`)
      .then(apply)
      .catch((err) => setError(err instanceof Error ? err.message : "Lead generator settings could not be loaded."));
  }, [orgId]);

  async function save() {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      apply(
        await api.put<LeadGenSettings>(`/operator/orgs/${orgId}/lead-generator`, {
          enabled,
          monthly_limit: Math.max(0, Math.round(Number(limit) || 0)),
          expires_at: expiryPayload(expires),
        }),
      );
      setNotice("Lead generator saved.");
      notifySaved("Lead generator saved.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Lead generator settings could not be saved.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <Stack gap={3}>
        <TipHeading
          label="Lead generator"
          tip="Paid add-on, not part of any plan. When enabled, the customer sees a LeadGen tab. Needs Organization AI enabled too."
        />
        {error && <Flash tone="error">{error}</Flash>}
        {notice && <Flash tone="success">{notice}</Flash>}
        <Cluster gap={2} align="end">
          <Checkbox label="Enabled" checked={enabled} onChange={(event) => setEnabled(event.target.checked)} />
          <FormField
            label="Leads per month"
            htmlFor="leadgen-monthly-limit"
            tooltip="Generated rows per calendar month (UTC), whether or not they convert."
          >
            <Input
              id="leadgen-monthly-limit"
              type="number"
              min={0}
              max={1000}
              value={limit}
              onChange={(event) => setLimit(event.target.value)}
            />
          </FormField>
          <ExpiryField id="leadgen-expires" value={expires} onChange={setExpires} />
          <Button onClick={() => void save()} disabled={saving || !row}>
            {saving ? "Saving…" : "Save lead generator"}
          </Button>
        </Cluster>
        {row ? <ExpiryNote expiresAt={row.expires_at} expired={row.expired} /> : null}
        {row && (
          <p className="admin-muted">
            {row.used_this_month} of {row.monthly_limit} generated this month.
          </p>
        )}
        {row?.ai_this_month && (
          <p className="admin-muted" data-testid="leadgen-ai-cost" title="Platform AI connection rates, this calendar month (UTC).">
            {leadGenAiLine(row.ai_this_month)}
          </p>
        )}
      </Stack>
    </Card>
  );
}

interface LoxleyKey {
  id: string;
  email: string | null;
  kind: "api_key" | "oauth_refresh";
  name: string;
  prefix: string;
  scope: "read" | "read_write";
  last_used_at: string | null;
}

interface LoxleySettings {
  enabled: boolean;
  switched_on: boolean;
  expires_at: string | null;
  expired: boolean;
  seats: number;
  seats_assigned: number;
  monthly_command_limit: number;
  connectors_enabled: boolean;
  mcp_global: boolean;
  usage: {
    app_turns: number;
    app_tool_calls: number;
    mcp_tool_calls: number;
    ai_calls: number;
    ai_tokens: number;
    ai_cost_usd: number;
  };
  keys: LoxleyKey[];
}

interface LoxleyTurn {
  id: string;
  user_id: string;
  channel: "app" | "mcp";
  created_at: string;
  duration_ms: number | null;
  page: string | null;
  message: string;
  reply: string | null;
  status: "ok" | "error";
  error_code: string | null;
  error_message: string | null;
  model: string | null;
  source: string | null;
  prompt_version: string | null;
  loaded_tabs: string[];
  steps: unknown[];
  model_calls: number;
  input_tokens: number;
  output_tokens: number;
  cost_micros: number;
  feedback: "up" | "down" | null;
  feedback_reason: string | null;
  feedback_comment: string | null;
}

/** Thumbs-down and failed Loxley answers with everything needed to see why: tools, model, prompt version, cost. */
function LoxleyTurnLog({ orgId }: { orgId: string }) {
  const [rows, setRows] = useState<LoxleyTurn[] | null>(null);
  const [filter, setFilter] = useState<"down" | "all">("down");
  const [error, setError] = useState<string | null>(null);

  async function load(next: "down" | "all") {
    setFilter(next);
    setError(null);
    try {
      const query = next === "down" ? "?feedback=down&limit=50" : "?limit=50";
      setRows(await api.get<LoxleyTurn[]>(`/operator/orgs/${orgId}/loxley/turns${query}`));
    } catch (err) {
      setError(err instanceof Error ? err.message : "The Loxley log could not be loaded.");
    }
  }

  return (
    <Stack gap={2}>
      <Cluster gap={2}>
        <Button size="sm" variant="secondary" onClick={() => void load("down")}>Show thumbs-down answers</Button>
        <Button size="sm" variant="ghost" onClick={() => void load("all")}>Show latest 50</Button>
      </Cluster>
      {error && <Flash tone="error">{error}</Flash>}
      {rows && !rows.length ? <p className="admin-muted">Nothing logged{filter === "down" ? " with a thumbs down" : ""}.</p> : null}
      {rows?.map((row) => (
        <details key={row.id} className="admin-json-details">
          <summary>
            {formatUtc(row.created_at)} · {row.channel === "mcp" ? "AI app" : "In app"} ·{" "}
            {row.feedback === "down" ? "Thumbs down" : row.feedback === "up" ? "Thumbs up" : "No rating"}
            {row.status === "error" ? ` · Error ${row.error_code ?? ""}` : ""} · {row.message.slice(0, 80)}
          </summary>
          <pre className="admin-json-view">{JSON.stringify(row, null, 2)}</pre>
        </details>
      ))}
    </Stack>
  );
}

/** Per-customer Loxley add-on (AI sales coach + MCP), priced per seat. Sits under Lead generator. */
function LoxleyCard({ orgId }: { orgId: string }) {
  const [row, setRow] = useState<LoxleySettings | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [seats, setSeats] = useState("0");
  const [expires, setExpires] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  function apply(next: LoxleySettings) {
    setRow(next);
    setEnabled(Boolean(next?.switched_on ?? next?.enabled));
    setSeats(String(next?.seats ?? 0));
    setExpires(expiryInputValue(next?.expires_at));
  }

  useEffect(() => {
    api
      .get<LoxleySettings>(`/operator/orgs/${orgId}/loxley`)
      .then(apply)
      .catch((err) => setError(err instanceof Error ? err.message : "Loxley settings could not be loaded."));
  }, [orgId]);

  async function save() {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      apply(
        await api.put<LoxleySettings>(`/operator/orgs/${orgId}/loxley`, {
          enabled,
          seats: Math.max(0, Math.round(Number(seats) || 0)),
          monthly_command_limit: row?.monthly_command_limit ?? 300,
          expires_at: expiryPayload(expires),
        }),
      );
      setNotice("Loxley saved.");
      notifySaved("Loxley saved.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Loxley settings could not be saved.");
    } finally {
      setSaving(false);
    }
  }

  async function revoke(key: LoxleyKey) {
    setError(null);
    try {
      await api.delete(`/operator/orgs/${orgId}/loxley/keys/${key.id}`);
      apply(await api.get<LoxleySettings>(`/operator/orgs/${orgId}/loxley`));
      setNotice(`${key.name} revoked.`);
      notifySaved(`${key.name} revoked.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "The key could not be revoked.");
    }
  }

  return (
    <Card>
      <Stack gap={3}>
        <TipHeading
          label="Loxley assistant"
          tip={
            "Paid add-on, priced per seat, outside every plan. Gives seated users the Loxley sales coach in the app "
            + "and lets them connect Claude, ChatGPT or other MCP apps. In-app requests share the organization's "
            + "monthly AI budget. There is no separate request count. MCP uses the customer's own model and is not "
            + "charged to that budget. MCP also needs the platform switch on the AI page."
          }
        />
        {error && <Flash tone="error">{error}</Flash>}
        {notice && <Flash tone="success">{notice}</Flash>}
        <Cluster gap={2} align="end">
          <Checkbox label="Enabled" checked={enabled} onChange={(event) => setEnabled(event.target.checked)} />
          <FormField label="Loxley seats" htmlFor="loxley-seats" tooltip="Seats bought. The org admin assigns them to people.">
            <Input id="loxley-seats" type="number" min={0} max={10000} value={seats}
              onChange={(event) => setSeats(event.target.value)} />
          </FormField>
          <ExpiryField id="loxley-expires" value={expires} onChange={setExpires} />
          <Button onClick={() => void save()} disabled={saving || !row}>
            {saving ? "Saving…" : "Save Loxley"}
          </Button>
        </Cluster>
        {row ? <ExpiryNote expiresAt={row.expires_at ?? null} expired={Boolean(row.expired)} /> : null}
        {row?.usage && (
          <p className="admin-muted" data-testid="loxley-usage">
            {row.seats_assigned} of {row.seats} seats assigned. This month: {row.usage.app_turns ?? 0} in-app requests
            ({formatAiUsd(row.usage.ai_cost_usd ?? 0)} AI cost, {(row.usage.ai_tokens ?? 0).toLocaleString("en-US")}{" "}
            tokens), {row.usage.mcp_tool_calls ?? 0} MCP tool calls. In-app requests share the organization AI budget.
            MCP calls use the customer's own AI and are not counted.
            {row.mcp_global ? "" : " MCP is switched off platform-wide."}
            {row.connectors_enabled ? "" : " The org admin has turned off external AI apps."}
          </p>
        )}
        <LoxleyTurnLog orgId={orgId} />
        {row?.keys?.length ? (
          <Stack gap={2}>
            <strong>Connected AI apps</strong>
            {row.keys.map((key) => (
              <Cluster key={key.id} gap={2} align="center" justify="between">
                <span>
                  {key.email}: {key.kind === "api_key" ? `${key.name} (${key.prefix}…)` : `${key.name} (OAuth)`},{" "}
                  {key.scope === "read" ? "read only" : "read and propose"}
                </span>
                <Button size="sm" variant="danger" onClick={() => void revoke(key)}>Revoke</Button>
              </Cluster>
            ))}
          </Stack>
        ) : null}
      </Stack>
    </Card>
  );
}

function CustomerUsageCard({ usage }: { usage: OrgUsage }) {
  const seatPct = usage.seats.fill_pct;
  const aiBudget = usage.ai.budget_cents;
  const aiUsedPct = aiBudget ? Math.min(100, Math.round((usage.ai.used_cents / aiBudget) * 100)) : 0;
  return (
    <Card>
      <Stack gap={4}>
        <Stack gap={2}>
          <TipHeading
            label="Usage"
            tip="Seats, AI spend, CRM volume, and estimated regional-database storage for this tenant."
          />
          <p className="admin-muted">
            Seats, AI spend, CRM volume, and estimated database storage for this tenant. Storage is row footprint in the regional database — not object-storage uploads.
          </p>
        </Stack>
        <div className="admin-usage-grid">
          <Card>
            <Stack gap={2}>
              <TipKicker
                label="Seats"
                tip="Assigned members versus licensed seats on the active CRM plan."
              />
              <p className="admin-kpi">{usage.seats.assigned}/{usage.seats.licensed}</p>
              <ProgressBar
                value={seatPct}
                tone={seatPct > 90 ? "warning" : "primary"}
                label={`${seatPct}% of licensed seats filled`}
              />
            </Stack>
          </Card>
          <Card>
            <Stack gap={2}>
              <TipKicker
                label="AI this month"
                tip="AI spend charged against this tenant’s monthly budget so far this calendar month."
              />
              <p className="admin-kpi">{money(usage.ai.used_cents, "USD")}</p>
              <ProgressBar
                value={aiUsedPct}
                tone={aiUsedPct > 90 ? "warning" : "primary"}
                label={
                  usage.ai.enabled
                    ? `${money(usage.ai.used_cents, "USD")} of ${money(aiBudget, "USD")}`
                    : "AI disabled"
                }
              />
            </Stack>
          </Card>
          <Card>
            <Stack gap={2}>
              <TipKicker
                label="CRM & workforce records"
                tip="Active row counts across core CRM and timesheet/expense tables."
              />
              <p className="admin-kpi">{usage.records_total.toLocaleString()}</p>
              <p className="admin-muted">Active rows across core tables</p>
            </Stack>
          </Card>
          <Card>
            <Stack gap={2}>
              <TipKicker
                label="Estimated storage"
                tip="Approximate row footprint in the regional database for this tenant — not file uploads."
              />
              <p className="admin-kpi">{formatBytes(usage.storage.bytes) || "0 B"}</p>
              <p className="admin-muted">
                {USAGE_STORAGE_LABELS.map((area, index) => (
                  <span key={area.key}>
                    {index > 0 ? " · " : ""}
                    {area.label} {formatBytes(usage.storage.by_area[area.key] ?? 0) || "0 B"}
                  </span>
                ))}
              </p>
            </Stack>
          </Card>
        </div>
        <table>
          <thead>
            <tr>
              <th>Record type</th>
              <th>Count</th>
            </tr>
          </thead>
          <tbody>
            {USAGE_RECORD_LABELS.map((row) => (
              <tr key={row.key}>
                <td>{row.label}</td>
                <td>{(usage.records[row.key] ?? 0).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Stack>
    </Card>
  );
}

function planKey(plan: Pick<PlanRow, "app_code" | "tier">): string {
  return `${plan.app_code}-${plan.tier}`;
}

function draftsFromPlans(rows: PlanRow[]): Record<string, { usd: string; seats: string }> {
  return Object.fromEntries(
    rows.map((plan) => [
      planKey(plan),
      { usd: (plan.amount_cents / 100).toFixed(2), seats: String(plan.seats) },
    ]),
  );
}

function seatBand(plan: PlanRow): string {
  const min = plan.min_seats ?? plan.seats;
  if (plan.max_seats == null) return `${min}+ users`;
  return `${min}–${plan.max_seats} users`;
}

const FRANKFURTER_LIVE_URLS = [
  "https://api.frankfurter.dev/v1/latest?base=USD&symbols=",
  "https://api.frankfurter.app/latest?from=USD&to=",
];

async function pullBrowserFxRates(symbols: string[]): Promise<Record<string, number>> {
  const list = symbols.filter((code) => code && code !== "USD");
  if (list.length === 0) return {};
  const joined = list.join(",");
  for (const prefix of FRANKFURTER_LIVE_URLS) {
    try {
      const res = await fetch(`${prefix}${joined}`);
      if (!res.ok) continue;
      const body = (await res.json()) as { rates?: Record<string, number> };
      const next: Record<string, number> = {};
      for (const code of list) {
        const value = Number(body.rates?.[code]);
        if (!(value > 0)) throw new Error(`missing ${code}`);
        next[code] = value;
      }
      return next;
    } catch {
      /* CSP or network — try the next URL, then fall through */
    }
  }
  throw new Error("Could not refresh conversion rates from the FX provider.");
}

export function CataloguePage() {
  const [plans, setPlans] = useState<PlanRow[]>([]);
  const [drafts, setDrafts] = useState<Record<string, { usd: string; seats: string }>>({});
  const [campaigns, setCampaigns] = useState<CampaignRow[]>([]);
  const [discountCodes, setDiscountCodes] = useState<DiscountCodeRow[]>([]);
  const [hookName, setHookName] = useState("Get 20% Off");
  const [hookText, setHookText] = useState("Sign up today for 1 year and save 20%");
  const [hookKind, setHookKind] = useState("percent_off");
  const [hookValue, setHookValue] = useState("20");
  const [hookTier, setHookTier] = useState("");
  const [hookStarts, setHookStarts] = useState("");
  const [hookEnds, setHookEnds] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [defaultDrafts, setDefaultDrafts] = useState<Record<string, CampaignDraft>>(DEFAULT_CAMPAIGN_DRAFTS);
  const [codeValue, setCodeValue] = useState("tenoff");
  const [codeName, setCodeName] = useState("10% off this payment");
  const [codeHook, setCodeHook] = useState("10% off this payment");
  const [codeEmail, setCodeEmail] = useState("");
  const [codeError, setCodeError] = useState<string | null>(null);
  const codeErrorRef = useRef<HTMLParagraphElement | null>(null);
  const [codeKind, setCodeKind] = useState("percent_off");
  const [codeAmount, setCodeAmount] = useState("10");
  const [codeMonths, setCodeMonths] = useState("");
  const [codeStarter, setCodeStarter] = useState(true);
  const [codePro, setCodePro] = useState(true);
  const [codeEnt, setCodeEnt] = useState(true);
  const [codeStarts, setCodeStarts] = useState(utcDateInput(0));
  const [codeEnds, setCodeEnds] = useState(utcDateInput(30));
  const [editingCodeId, setEditingCodeId] = useState<string | null>(null);
  const [planCopy, setPlanCopy] = useState<PlanCopyStatus | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [fx, setFx] = useState<FxRates | null>(null);
  const [rateDrafts, setRateDrafts] = useState<Record<string, string>>({});
  const [markets, setMarkets] = useState<BillingMarket[]>([]);
  const [marketDrafts, setMarketDrafts] = useState<Record<string, BillingMarket>>({});
  const [addCountry, setAddCountry] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [fxBusy, setFxBusy] = useState(false);
  const [marketsBusy, setMarketsBusy] = useState(false);

  function applyFx(next: FxRates | undefined) {
    if (!next?.rates) return;
    setFx(next);
    const drafts: Record<string, string> = {};
    for (const [code, value] of Object.entries(next.rates)) {
      drafts[code] = String(value);
    }
    setRateDrafts(drafts);
  }

  function applyMarkets(next: BillingMarket[] | undefined) {
    if (!next) return;
    setMarkets(next);
    const drafts: Record<string, BillingMarket> = {};
    for (const row of next) {
      drafts[row.country_code] = { ...row };
    }
    setMarketDrafts(drafts);
  }

  function applyPlans(next: PlanRow[], resetDrafts: boolean) {
    const crm = next.filter((plan) => plan.app_code === "crm");
    setPlans(crm);
    if (resetDrafts) setDrafts(draftsFromPlans(crm));
  }

  function updateDraft(plan: PlanRow, patch: Partial<{ usd: string; seats: string }>) {
    setNotice(null);
    setDrafts((current) => ({
      ...current,
      [planKey(plan)]: { usd: current[planKey(plan)]?.usd ?? "", seats: current[planKey(plan)]?.seats ?? "5", ...patch },
    }));
  }

  function updateMarketDraft(code: string, patch: Partial<BillingMarket>) {
    setNotice(null);
    setMarketDrafts((current) => {
      const base = current[code] ?? markets.find((row) => row.country_code === code);
      if (!base) return current;
      return { ...current, [code]: { ...base, ...patch } };
    });
  }

  function planPriceForCurrency(plan: PlanRow, currency: string): string {
    if (currency === "USD") return money(plan.amount_cents, "USD");
    const local = plan.prices?.[currency];
    return local ? money(local.amount_cents, currency) : "—";
  }

  async function load() {
    try {
      const row = await api.get<{
        plans: PlanRow[];
        fx?: FxRates;
        markets?: BillingMarket[];
        campaigns?: CampaignRow[];
        discount_codes?: DiscountCodeRow[];
        plan_copy?: PlanCopyStatus;
      }>("/operator/orgs");
      applyPlans(row.plans, true);
      applyFx(row.fx);
      applyMarkets(row.markets);
      setCampaigns(row.campaigns ?? []);
      setDiscountCodes(row.discount_codes ?? []);
      setPlanCopy(row.plan_copy ?? null);
      const nextDefaults = { ...DEFAULT_CAMPAIGN_DRAFTS };
      for (const campaign of row.campaigns ?? []) {
        if (!campaignIsDefault(campaign) || !campaign.tier) continue;
        nextDefaults[campaign.tier] = {
          name: campaign.name,
          hook: campaign.hook,
          discount_kind: campaign.discount_kind,
          discount_value: String(campaign.discount_value),
        };
      }
      setDefaultDrafts(nextDefaults);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Catalogue could not be loaded.");
    }
  }

  function resetCampaignForm() {
    setEditingId(null);
    setHookName("Get 20% Off");
    setHookText("Sign up today for 1 year and save 20%");
    setHookKind("percent_off");
    setHookValue("20");
    setHookTier("");
    setHookStarts("");
    setHookEnds("");
  }

  function applyCampaignResult(
    row: { campaigns: CampaignRow[]; plans: PlanRow[]; plan_copy?: PlanCopyStatus },
    noticeText: string,
  ) {
    setCampaigns(row.campaigns);
    applyPlans(row.plans, false);
    if (row.plan_copy) setPlanCopy(row.plan_copy);
    const nextDefaults = { ...DEFAULT_CAMPAIGN_DRAFTS };
    for (const campaign of row.campaigns) {
      if (!campaignIsDefault(campaign) || !campaign.tier) continue;
      nextDefaults[campaign.tier] = {
        name: campaign.name,
        hook: campaign.hook,
        discount_kind: campaign.discount_kind,
        discount_value: String(campaign.discount_value),
      };
    }
    setDefaultDrafts(nextDefaults);
    setNotice(noticeText);
    if (noticeText) notifySaved(noticeText);
  }

  function campaignPayload(input: {
    name: string;
    hook: string;
    discount_kind: string;
    discount_value: number;
    tier: string | null;
    active: boolean;
    starts_at: string | null;
    ends_at: string | null;
  }) {
    return {
      name: input.name,
      hook: input.hook,
      discount_kind: input.discount_kind,
      discount_value: input.discount_value,
      interval: "year",
      app_code: "crm",
      tier: input.tier,
      active: input.active,
      starts_at: input.starts_at,
      ends_at: input.ends_at,
    };
  }

  async function persistCampaign(
    body: ReturnType<typeof campaignPayload>,
    campaignId: string | null,
    noticeText: string,
  ) {
    const row = campaignId
      ? await api.put<{ campaigns: CampaignRow[]; plans: PlanRow[] }>(`/operator/campaigns/${campaignId}`, body)
      : await api.post<{ campaigns: CampaignRow[]; plans: PlanRow[] }>("/operator/campaigns", body);
    applyCampaignResult(row, noticeText);
  }

  async function saveCampaign() {
    setError(null);
    setNotice(null);
    const value = Number(hookValue);
    if (!Number.isInteger(value) || value < 1) {
      setError("Discount value must be a whole number of 1 or more.");
      return;
    }
    if (!hookStarts || !hookEnds) {
      setError("Timed campaigns need a start date and an end date. Use Default fallbacks for always-on copy.");
      return;
    }
    if (hookEnds < hookStarts) {
      setError("Campaign end date must be on or after the start date.");
      return;
    }
    try {
      await persistCampaign(
        campaignPayload({
          name: hookName,
          hook: hookText,
          discount_kind: hookKind,
          discount_value: value,
          tier: hookTier || null,
          active: true,
          starts_at: startOfUtcDay(hookStarts),
          ends_at: endOfUtcDay(hookEnds),
        }),
        editingId,
        "Campaign saved as a draft. Publish plan copy below to send this hook to the pricing page.",
      );
      resetCampaignForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Campaign save failed");
    }
  }

  async function saveDefaultCampaign(tier: string) {
    setError(null);
    setNotice(null);
    const draft = defaultDrafts[tier];
    const value = Number(draft?.discount_value);
    if (!draft?.name.trim() || !draft.hook.trim()) {
      setError("Default name and hook copy are required.");
      return;
    }
    if (!Number.isInteger(value) || value < 1) {
      setError("Discount value must be a whole number of 1 or more.");
      return;
    }
    const existing = campaigns.find((row) => campaignIsDefault(row) && row.tier === tier) ?? null;
    try {
      await persistCampaign(
        campaignPayload({
          name: draft.name,
          hook: draft.hook,
          discount_kind: draft.discount_kind,
          discount_value: value,
          tier,
          active: true,
          starts_at: null,
          ends_at: null,
        }),
        existing?.id ?? null,
        "Default fallback saved. Publish plan copy below to send this hook to the pricing page.",
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Default fallback save failed");
    }
  }

  async function toggleCampaign(row: CampaignRow) {
    setError(null);
    setNotice(null);
    try {
      await persistCampaign(
        campaignPayload({
          name: row.name,
          hook: row.hook,
          discount_kind: row.discount_kind,
          discount_value: row.discount_value,
          tier: row.tier,
          active: !row.active,
          starts_at: row.starts_at ?? null,
          ends_at: row.ends_at ?? null,
        }),
        row.id,
        row.active
          ? "Campaign disabled. Publish plan copy below if the live pricing page should drop this hook."
          : "Campaign enabled. Publish plan copy below to send it to the pricing page.",
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not update campaign");
    }
  }

  function resetCodeForm() {
    setEditingCodeId(null);
    setCodeValue("tenoff");
    setCodeName("10% off this payment");
    setCodeHook("10% off this payment");
    setCodeEmail("");
    setCodeKind("percent_off");
    setCodeAmount("10");
    setCodeMonths("");
    setCodeStarter(true);
    setCodePro(true);
    setCodeEnt(true);
    setCodeStarts(utcDateInput(0));
    setCodeEnds(utcDateInput(30));
    setCodeError(null);
  }

  function discountPayload(active: boolean) {
    return {
      code: codeValue,
      name: codeName,
      hook: codeHook,
      bound_email: codeEmail.trim() || null,
      discount_kind: codeKind,
      discount_value: Number(codeAmount),
      discount_months: codeKind === "percent_off" && codeMonths.trim() ? Number(codeMonths) : null,
      applies_starter: codeStarter,
      applies_professional: codePro,
      applies_enterprise: codeEnt,
      active,
      starts_at: startOfUtcDay(codeStarts),
      ends_at: endOfUtcDay(codeEnds),
    };
  }

  function failCode(message: string) {
    setCodeError(message);
    setNotice(null);
    window.requestAnimationFrame(() => codeErrorRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" }));
  }

  function validateDiscountForm(): string | null {
    if (!/^[A-Za-z0-9_-]{3,32}$/.test(codeValue.trim())) {
      return "Code: use 3–32 letters, numbers, underscores or hyphens (no spaces).";
    }
    if (codeName.trim().length < 2) return "Name: give the code a name of at least 2 characters.";
    if (codeHook.trim().length < 4) {
      return "Hook: copy is required (at least 4 characters). Buyers see this after they apply the code.";
    }
    if (codeEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(codeEmail.trim())) {
      return "Bound email: enter a valid email address, or leave it blank for a campaign code.";
    }
    const value = Number(codeAmount);
    if (!codeAmount.trim() || !Number.isInteger(value) || value < 1) {
      return `${codeKind === "free_months" ? "Free months" : "Percent"}: enter a whole number of 1 or more.`;
    }
    if (codeKind === "percent_off" && value > 90) return "Percent: must be between 1 and 90.";
    if (codeKind === "free_months" && value > 11) return "Free months: must be between 1 and 11.";
    if (codeKind === "percent_off" && codeMonths.trim()) {
      const months = Number(codeMonths);
      if (!Number.isInteger(months) || months < 1 || months > 11) {
        return "For how many months: enter a whole number from 1 to 11, or leave blank for the whole payment.";
      }
    }
    if (!codeStarts || !codeEnds) return "Dates: choose both a start date and an end date.";
    if (codeEnds < codeStarts) return "End date: must be on or after the start date.";
    if (!codeStarter && !codePro && !codeEnt) return "Plans: select at least one plan for this discount code.";
    return null;
  }

  async function saveDiscountCode(active = true) {
    setError(null);
    setNotice(null);
    setCodeError(null);
    const problem = validateDiscountForm();
    if (problem) {
      failCode(problem);
      return;
    }
    const body = { ...discountPayload(active), discount_value: Number(codeAmount) };
    try {
      const row = editingCodeId
        ? await api.put<{ discount_codes: DiscountCodeRow[] }>(`/operator/discount-codes/${editingCodeId}`, body)
        : await api.post<{ discount_codes: DiscountCodeRow[] }>("/operator/discount-codes", body);
      setDiscountCodes(row.discount_codes);
      resetCodeForm();
      setNotice("Discount code saved. It does not stack with another code. Renewals bill full list price.");
      notifySaved("Discount code saved.");
    } catch (err) {
      const message = err instanceof Error && err.message ? err.message : "Discount code save failed";
      failCode(`Not saved: ${message}`);
    }
  }

  async function toggleDiscountCode(row: DiscountCodeRow) {
    setError(null);
    setNotice(null);
    try {
      const next = await api.put<{ discount_codes: DiscountCodeRow[] }>(`/operator/discount-codes/${row.id}`, {
        code: row.code,
        name: row.name,
        hook: row.hook,
        bound_email: row.bound_email,
        discount_kind: row.discount_kind,
        discount_value: row.discount_value,
        applies_starter: row.applies_starter,
        applies_professional: row.applies_professional,
        applies_enterprise: row.applies_enterprise,
        active: !row.active,
        starts_at: row.starts_at,
        ends_at: row.ends_at,
      });
      setDiscountCodes(next.discount_codes);
      setNotice(row.active ? "Discount code disabled." : "Discount code enabled.");
      notifySaved(row.active ? "Discount code disabled." : "Discount code enabled.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not update discount code");
    }
  }

  async function publishPlanCopy() {
    setError(null);
    setNotice(null);
    setPublishing(true);
    try {
      const row = await api.post<{ plan_copy: PlanCopyStatus; plans: PlanRow[]; campaigns: CampaignRow[] }>(
        "/operator/plan-copy/publish",
        {},
      );
      setPlanCopy(row.plan_copy);
      applyPlans(row.plans, false);
      if (row.campaigns) setCampaigns(row.campaigns);
      setNotice("Published. The pricing page now shows this plan copy. If that config is unavailable, the page falls back to the built-in default hooks.");
      notifySaved("Plan copy published.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not publish plan copy");
    } finally {
      setPublishing(false);
    }
  }

  async function savePlans() {
    setError(null);
    setNotice(null);
    const payload: Array<{ app_code: string; tier: string; amount_cents: number; trial_days: number; seats: number }> = [];
    for (const plan of plans) {
      const draft = drafts[planKey(plan)];
      const dollars = Number(draft?.usd);
      if (!Number.isFinite(dollars) || dollars < 0) {
        setError(`${plan.tier}: USD must be a number 0 or greater.`);
        return;
      }
      payload.push({
        app_code: plan.app_code,
        tier: plan.tier,
        amount_cents: Math.round(dollars * 100),
        trial_days: plan.trial_days,
        seats: plan.app_code === "crm" ? 1 : Number(draft?.seats) || plan.seats,
      });
    }
    setSaving(true);
    try {
      const row = await api.put<{ plans: PlanRow[]; fx?: FxRates; markets?: BillingMarket[] }>(
        "/operator/plans",
        { plans: payload },
      );
      applyPlans(row.plans, true);
      applyFx(row.fx);
      applyMarkets(row.markets);
      setNotice("Plans saved.");
      notifySaved("Plans saved.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Plan save failed");
    } finally {
      setSaving(false);
    }
  }

  async function saveMarkets(next: BillingMarket[]) {
    setError(null);
    setNotice(null);
    setMarketsBusy(true);
    try {
      const row = await api.put<{ markets: BillingMarket[]; fx: FxRates; plans: PlanRow[] }>(
        "/operator/markets",
        { markets: next },
      );
      applyMarkets(row.markets);
      applyFx(row.fx);
      applyPlans(row.plans, false);
      setNotice("Country markets saved.");
      notifySaved("Country markets saved.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save country markets");
    } finally {
      setMarketsBusy(false);
    }
  }

  async function persistMarketDrafts() {
    const payload = Object.values(marketDrafts);
    for (const row of payload) {
      const percent = Number(row.tax_percent);
      if (!Number.isFinite(percent) || percent < 0 || percent > 100) {
        setError(`${row.country_code}: tax percent must be between 0 and 100.`);
        return;
      }
      if (!/^[A-Z]{3}$/.test(row.currency)) {
        setError(`${row.country_code}: currency must be a three-letter code.`);
        return;
      }
    }
    await saveMarkets(
      payload.map((row) => ({
        ...row,
        tax_percent: Number(row.tax_percent) || 0,
        tax_label: String(row.tax_label || "").trim(),
        currency: row.currency.toUpperCase(),
      })),
    );
  }

  function addMarketCountry() {
    const code = addCountry.trim().toUpperCase();
    if (!code || marketDrafts[code]) {
      setError(code ? `${code} is already in the catalogue.` : "Choose a country to add.");
      return;
    }
    const currency = COUNTRY_CURRENCY[code] || "USD";
    const next: BillingMarket = {
      country_code: code,
      name: countryLabel(code).replace(/ \([A-Z]{2}\)$/, ""),
      currency,
      tax_label: "",
      tax_percent: 0,
    };
    setMarketDrafts((current) => ({ ...current, [code]: next }));
    setMarkets((current) => [...current, next]);
    setAddCountry("");
    setNotice(`Added ${next.name}. Set tax and save countries.`);
  }

  function removeMarketCountry(code: string) {
    if (code === "US") {
      setError("United States is the USD base market and cannot be removed.");
      return;
    }
    setMarketDrafts((current) => {
      const next = { ...current };
      delete next[code];
      return next;
    });
    setMarkets((current) => current.filter((row) => row.country_code !== code));
  }

  useEffect(() => {
    void load();
  }, []);

  return (
    <Stack gap={4}>
      <TipHeading
        as="h2"
        label="Plan catalogue"
        tip="USD seat prices, country FX/tax, marketing hooks, discount codes, and the public pricing preview."
      />
      <p className="admin-muted">
        CRM catalogue prices are USD per seat per month. Buyer country (from IP on the marketing buy page)
        picks currency and tax. Tax label and percent live on each country row below — that is platform
        checkout tax, separate from each tenant&apos;s document tax rate in org settings.
      </p>
      {error && <Flash tone="error">{error}</Flash>}
      {notice && <Flash tone="success">{notice}</Flash>}
      <Card>
        <Stack gap={3}>
          <TipHeading
            label="USD plan prices"
            tip="Base USD price per seat per month. Local columns on the country table convert with FX."
          />
          <p className="admin-muted">Edit the USD base; local columns on the country table convert with FX.</p>
          <table>
            <thead>
              <tr>
                <th>Plan</th>
                <th>USD / seat / month</th>
                <th>Users</th>
              </tr>
            </thead>
            <tbody>
              {plans.map((plan) => {
                const draft = drafts[planKey(plan)] ?? {
                  usd: (plan.amount_cents / 100).toFixed(2),
                  seats: "5",
                };
                return (
                  <tr key={planKey(plan)}>
                    <td>{plan.display_name ?? plan.tier}</td>
                    <td>
                      <Input
                        value={draft.usd}
                        inputMode="decimal"
                        aria-label={`${plan.tier} USD`}
                        onChange={(event) => updateDraft(plan, { usd: event.target.value })}
                      />
                    </td>
                    <td>{seatBand(plan)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Cluster gap={2} align="center">
            <Button onClick={() => savePlans()} disabled={saving || plans.length === 0}>
              {saving ? "Saving…" : "Save plans"}
            </Button>
          </Cluster>
        </Stack>
      </Card>
      <Card>
        <Stack gap={3}>
          <TipHeading
            label="Countries, FX, and tax"
            tip="Buyer country rows drive checkout currency, FX conversion, and tax label/percent on the buy page."
          />
          <p className="admin-muted">{formatFxStatus(fx)}</p>
          <p className="admin-muted">
            Rows are countries. Plan columns show local per-seat price. Tax description (GST, VAT, …) and
            percent appear on the buy page and are included in the amount charged.
          </p>
          <div className="admin-table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Country</th>
                  <th>Currency</th>
                  <th>USD → rate</th>
                  <th>Tax label</th>
                  <th>Tax %</th>
                  {plans.map((plan) => (
                    <th key={planKey(plan)}>{plan.display_name ?? plan.tier}</th>
                  ))}
                  <th />
                </tr>
              </thead>
              <tbody>
                {Object.values(marketDrafts)
                  .sort((a, b) => (a.country_code === "US" ? -1 : b.country_code === "US" ? 1 : a.name.localeCompare(b.name)))
                  .map((row) => {
                    const rateKey = row.currency === "USD" ? null : row.currency;
                    return (
                      <tr key={row.country_code}>
                        <td>{row.name} ({row.country_code})</td>
                        <td>
                          <Input
                            value={row.currency}
                            aria-label={`${row.country_code} currency`}
                            onChange={(event) =>
                              updateMarketDraft(row.country_code, { currency: event.target.value.toUpperCase() })
                            }
                          />
                        </td>
                        <td>
                          {rateKey ? (
                            <Input
                              value={rateDrafts[rateKey] ?? ""}
                              inputMode="decimal"
                              aria-label={`USD to ${rateKey}`}
                              onChange={(event) =>
                                setRateDrafts((current) => ({ ...current, [rateKey]: event.target.value }))
                              }
                            />
                          ) : (
                            "1"
                          )}
                        </td>
                        <td>
                          <Input
                            value={row.tax_label}
                            placeholder="GST, VAT…"
                            aria-label={`${row.country_code} tax label`}
                            onChange={(event) =>
                              updateMarketDraft(row.country_code, { tax_label: event.target.value })
                            }
                          />
                        </td>
                        <td>
                          <Input
                            value={String(row.tax_percent ?? "")}
                            inputMode="decimal"
                            aria-label={`${row.country_code} tax percent`}
                            onChange={(event) =>
                              updateMarketDraft(row.country_code, {
                                tax_percent: event.target.value,
                              })
                            }
                          />
                        </td>
                        {plans.map((plan) => (
                          <td key={planKey(plan)}>{planPriceForCurrency(plan, row.currency)}</td>
                        ))}
                        <td>
                          {row.country_code !== "US" ? (
                            <Button variant="secondary" onClick={() => removeMarketCountry(row.country_code)}>
                              Remove
                            </Button>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
          <Cluster gap={2} align="end">
            <FormField
              label="Add country"
              tooltip="Adds a market row with a suggested currency. Save countries to persist it."
            >
              <Select
                value={addCountry}
                aria-label="Add country"
                onChange={(event) => setAddCountry(event.target.value)}
              >
                <option value="">Select…</option>
                {ALL_COUNTRY_CODES.filter((code) => !marketDrafts[code])
                  .map((code) => ({ code, label: countryLabel(code) }))
                  .sort((x, y) => x.label.localeCompare(y.label))
                  .map(({ code, label }) => (
                    <option key={code} value={code}>
                      {label}
                    </option>
                  ))}
              </Select>
            </FormField>
            <Button variant="secondary" onClick={() => addMarketCountry()} disabled={!addCountry}>
              Add country
            </Button>
            <Button onClick={() => persistMarketDrafts()} disabled={marketsBusy}>
              {marketsBusy ? "Saving…" : "Save countries"}
            </Button>
            <Button
              variant="secondary"
              disabled={fxBusy}
              onClick={() => {
                setError(null);
                setNotice(null);
                setFxBusy(true);
                const rates: Record<string, number> = {};
                for (const [code, raw] of Object.entries(rateDrafts)) {
                  const value = Number(raw);
                  if (value > 0) rates[code] = value;
                }
                void api
                  .put<{ fx: FxRates; plans: PlanRow[]; markets?: BillingMarket[] }>("/operator/fx", { rates })
                  .then((row) => {
                    applyFx(row.fx);
                    applyPlans(row.plans, false);
                    applyMarkets(row.markets);
                    setNotice("Rates saved.");
                    notifySaved("Rates saved.");
                  })
                  .catch((err) => setError(err instanceof Error ? err.message : "Could not save FX rates"))
                  .finally(() => setFxBusy(false));
              }}
            >
              Save rates
            </Button>
            <Button
              disabled={fxBusy}
              onClick={() => {
                setError(null);
                setNotice(null);
                setFxBusy(true);
                void (async () => {
                  try {
                    try {
                      const row = await api.post<{ fx: FxRates; plans: PlanRow[]; markets?: BillingMarket[] }>(
                        "/operator/fx/refresh",
                        {},
                      );
                      applyFx(row.fx);
                      applyPlans(row.plans, false);
                      applyMarkets(row.markets);
                      setNotice("Live rates updated.");
                      notifySaved("Live rates updated.");
                      return;
                    } catch {
                      const symbols = Object.keys(rateDrafts);
                      const rates = await pullBrowserFxRates(symbols);
                      const row = await api.put<{ fx: FxRates; plans: PlanRow[]; markets?: BillingMarket[] }>(
                        "/operator/fx",
                        { rates },
                      );
                      applyFx(row.fx);
                      applyPlans(row.plans, false);
                      applyMarkets(row.markets);
                      setNotice("Live rates updated.");
                      notifySaved("Live rates updated.");
                    }
                  } catch (err) {
                    setError(err instanceof Error ? err.message : "Could not refresh FX rates");
                  } finally {
                    setFxBusy(false);
                  }
                })();
              }}
            >
              {fxBusy ? "Pulling…" : "Pull live rates"}
            </Button>
          </Cluster>
        </Stack>
      </Card>
      <Card>
        <Stack gap={4}>
          <Stack gap={2}>
            <TipHeading
              label="Default fallbacks"
              tip="Always-on hook copy for each plan card when no timed signup campaign is live."
            />
            <p className="admin-muted">{BILLING_COPY_NOTE}</p>
            <p className="admin-muted">
              Always-on hook copy for each plan. Timed signup campaigns below override these while they are live
              in this draft. The public pricing page only shows what you publish at the bottom of this screen.
            </p>
          </Stack>
          <Grid columns={1} gap={4} className="admin-campaign-defaults">
            {CAMPAIGN_PLAN_OPTIONS.map((plan) => {
              const draft = defaultDrafts[plan.id] ?? DEFAULT_CAMPAIGN_DRAFTS[plan.id];
              return (
                <Stack key={plan.id} gap={3} className="admin-campaign-form">
                  <h4>{plan.label}</h4>
                  <FormField
                    label="Name"
                    tooltip="Internal label for this plan’s default hook. Not shown as the buyer’s headline."
                  >
                    <Input
                      value={draft.name}
                      onChange={(event) =>
                        setDefaultDrafts((current) => ({
                          ...current,
                          [plan.id]: { ...draft, name: event.target.value },
                        }))
                      }
                    />
                  </FormField>
                  <FormField
                    label="Hook"
                    tooltip="Marketing copy on the pricing card when no timed campaign overrides this plan."
                  >
                    <Textarea
                      value={draft.hook}
                      maxLength={400}
                      rows={4}
                      aria-label={`${plan.label} default hook`}
                      onChange={(event) =>
                        setDefaultDrafts((current) => ({
                          ...current,
                          [plan.id]: { ...draft, hook: event.target.value },
                        }))
                      }
                    />
                  </FormField>
                  <Grid columns={2} gap={3}>
                    <FormField
                      label="Offer"
                      tooltip="Display offer type on the card. Checkout still bills list price unless a code is used."
                    >
                      <Select
                        value={draft.discount_kind}
                        aria-label={`${plan.label} default offer`}
                        onChange={(event) =>
                          setDefaultDrafts((current) => ({
                            ...current,
                            [plan.id]: { ...draft, discount_kind: event.target.value },
                          }))
                        }
                      >
                        <option value="free_months">Buy 12 months, get N free</option>
                        <option value="percent_off">Percent off</option>
                      </Select>
                    </FormField>
                    <FormField
                      label="Value"
                      tooltip="Free months or percent shown with the offer. Marketing display only unless a code applies."
                    >
                      <Input
                        value={draft.discount_value}
                        aria-label={`${plan.label} default value`}
                        onChange={(event) =>
                          setDefaultDrafts((current) => ({
                            ...current,
                            [plan.id]: { ...draft, discount_value: event.target.value },
                          }))
                        }
                      />
                    </FormField>
                  </Grid>
                  <Cluster gap={2}>
                    <Button onClick={() => saveDefaultCampaign(plan.id)}>Save {plan.label} default</Button>
                    {campaigns.find((row) => campaignIsDefault(row) && row.tier === plan.id) && (
                      <Button
                        variant="secondary"
                        onClick={() => {
                          const row = campaigns.find((item) => campaignIsDefault(item) && item.tier === plan.id);
                          if (row) void toggleCampaign(row);
                        }}
                      >
                        {(campaigns.find((row) => campaignIsDefault(row) && row.tier === plan.id)?.active ?? true)
                          ? "Disable"
                          : "Enable"}
                      </Button>
                    )}
                  </Cluster>
                </Stack>
              );
            })}
          </Grid>
        </Stack>
      </Card>
      <Card>
        <Stack gap={3}>
          <TipHeading
            label="Signup campaigns"
            tip="Time-boxed marketing hooks that override plan defaults while live. Publish before the site shows them."
          />
          <p className="admin-muted">
            Time-boxed marketing hooks only. While live in this draft they replace the default on matching plan
            cards after you publish. Disable leaves the row in place and falls back to the default. Removing
            deletes it. Checkout still bills full list price unless the buyer enters a discount code.
          </p>
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Hook</th>
                <th>Offer</th>
                <th>Plan</th>
                <th>Dates</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {campaigns.filter((row) => !campaignIsDefault(row)).length === 0 && (
                <tr><td colSpan={7} className="admin-muted">No timed campaigns yet.</td></tr>
              )}
              {campaigns.filter((row) => !campaignIsDefault(row)).map((row) => (
                <tr key={row.id}>
                  <td>{row.name}</td>
                  <td>{row.hook}</td>
                  <td>{campaignOfferLabel(row)}</td>
                  <td>{row.tier ? (CAMPAIGN_PLAN_OPTIONS.find((plan) => plan.id === row.tier)?.label ?? row.tier) : "All plans"}</td>
                  <td>
                    {isoDateInput(row.starts_at) || "—"}
                    {" → "}
                    {isoDateInput(row.ends_at) || "—"}
                  </td>
                  <td><StatusBadge status={row.active ? "active" : "suspended"} /></td>
                  <td>
                    <Cluster gap={2}>
                      <Button
                        variant="secondary"
                        onClick={() => {
                          setEditingId(row.id);
                          setHookName(row.name);
                          setHookText(row.hook);
                          setHookKind(row.discount_kind);
                          setHookValue(String(row.discount_value));
                          setHookTier(row.tier ?? "");
                          setHookStarts(isoDateInput(row.starts_at));
                          setHookEnds(isoDateInput(row.ends_at));
                        }}
                      >
                        Edit
                      </Button>
                      <Button variant="secondary" onClick={() => toggleCampaign(row)}>
                        {row.active ? "Disable" : "Enable"}
                      </Button>
                      <Button
                        variant="danger"
                        onClick={() => {
                          setError(null);
                          void api
                            .delete<{ campaigns: CampaignRow[] }>(`/operator/campaigns/${row.id}`)
                            .then((next) => {
                              setCampaigns(next.campaigns);
                              if (editingId === row.id) resetCampaignForm();
                            })
                            .catch((err) => setError(err instanceof Error ? err.message : "Could not remove campaign"));
                        }}
                      >
                        Remove
                      </Button>
                    </Cluster>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Stack gap={3} className="admin-campaign-form">
            <FormField
              label="Name"
              tooltip="Operator label for this campaign row. Helps you find it in the table."
            >
              <Input value={hookName} onChange={(event) => setHookName(event.target.value)} />
            </FormField>
            <FormField
              label="Hook"
              tooltip="Buyer-facing copy on matching plan cards while this campaign is live and published."
            >
              <Textarea
                value={hookText}
                maxLength={400}
                rows={4}
                aria-label="Campaign hook"
                onChange={(event) => setHookText(event.target.value)}
              />
            </FormField>
            <Grid columns={2} gap={3}>
              <FormField
                label="Offer"
                tooltip="Display offer type. Does not change checkout price unless the buyer uses a discount code."
              >
                <Select value={hookKind} onChange={(event) => setHookKind(event.target.value)}>
                  <option value="free_months">Buy 12 months, get N free</option>
                  <option value="percent_off">Percent off</option>
                </Select>
              </FormField>
              <FormField
                label="Value"
                tooltip="Free months or percent shown with the offer on the pricing card."
              >
                <Input value={hookValue} onChange={(event) => setHookValue(event.target.value)} />
              </FormField>
              <FormField
                label="Plan"
                tooltip="Which plan card this campaign overrides. Blank applies to all plans."
              >
                <Select value={hookTier} onChange={(event) => setHookTier(event.target.value)}>
                  <option value="">All plans</option>
                  <option value="starter">Basic</option>
                  <option value="professional">Pro</option>
                  <option value="enterprise">Enterprise</option>
                </Select>
              </FormField>
              <FormField
                label="Start date"
                tooltip="First day this campaign can override the default hook (UTC date)."
              >
                <Input type="date" value={hookStarts} onChange={(event) => setHookStarts(event.target.value)} />
              </FormField>
              <FormField
                label="End date"
                tooltip="Last day this campaign stays live. After this date the default hook returns."
              >
                <Input type="date" value={hookEnds} onChange={(event) => setHookEnds(event.target.value)} />
              </FormField>
            </Grid>
            <Cluster gap={2}>
              <Button onClick={() => saveCampaign()}>{editingId ? "Update campaign" : "Add campaign"}</Button>
              {editingId && (
                <Button variant="secondary" onClick={() => resetCampaignForm()}>Cancel</Button>
              )}
            </Cluster>
            {codeError ? (
              <p ref={codeErrorRef} className="admin-error">{codeError}</p>
            ) : null}
          </Stack>
        </Stack>
      </Card>
      <Card>
        <Stack gap={3}>
          <TipHeading
            label="Discount codes"
            tip="One-time checkout codes bound to an email. They change the invoice price; campaigns do not."
          />
          <p className="admin-muted">
            Buyers enter a code on the website pay page or in Plan &amp; Billing. Codes do not stack — one code
            per checkout. A code bound to an email is single-use; leave the email blank for a
            campaign code that any customer can use until the end date. Renewals bill full list price.
            The code auto-disables when its end date passes, or after a bound code is used. The hook
            is the copy they see after they apply it.
          </p>
          <table>
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Hook</th>
                <th>Email</th>
                <th>Offer</th>
                <th>Plans</th>
                <th>Dates</th>
                <th>Used</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {discountCodes.length === 0 && (
                <tr><td colSpan={10} className="admin-muted">No discount codes yet.</td></tr>
              )}
              {discountCodes.map((row) => (
                <tr key={row.id}>
                  <td><code>{row.code}</code></td>
                  <td>{row.name}</td>
                  <td>{row.hook}</td>
                  <td>{row.bound_email || "—"}</td>
                  <td>{discountOfferLabel(row)}</td>
                  <td>
                    {[
                      row.applies_starter ? "Basic" : null,
                      row.applies_professional ? "Pro" : null,
                      row.applies_enterprise ? "Enterprise" : null,
                    ].filter(Boolean).join(", ")}
                  </td>
                  <td>
                    {isoDateInput(row.starts_at) || "—"}
                    {" → "}
                    {isoDateInput(row.ends_at) || "—"}
                  </td>
                  <td>{row.redeemed_count}</td>
                  <td><StatusBadge status={row.active ? "active" : "suspended"} /></td>
                  <td>
                    <Cluster gap={2}>
                      <Button
                        variant="secondary"
                        onClick={() => {
                          setEditingCodeId(row.id);
                          setCodeValue(row.code);
                          setCodeName(row.name);
                          setCodeHook(row.hook || row.name);
                          setCodeEmail(row.bound_email || "");
                          setCodeKind(row.discount_kind);
                          setCodeAmount(String(row.discount_value));
                          setCodeMonths(row.discount_months ? String(row.discount_months) : "");
                          setCodeStarter(row.applies_starter);
                          setCodePro(row.applies_professional);
                          setCodeEnt(row.applies_enterprise);
                          setCodeStarts(isoDateInput(row.starts_at) || utcDateInput(0));
                          setCodeEnds(isoDateInput(row.ends_at) || utcDateInput(30));
                        }}
                      >
                        Edit
                      </Button>
                      <Button variant="secondary" onClick={() => toggleDiscountCode(row)}>
                        {row.active ? "Disable" : "Enable"}
                      </Button>
                      <Button
                        variant="danger"
                        onClick={() => {
                          setError(null);
                          void api
                            .delete<{ discount_codes: DiscountCodeRow[] }>(`/operator/discount-codes/${row.id}`)
                            .then((next) => {
                              setDiscountCodes(next.discount_codes);
                              if (editingCodeId === row.id) resetCodeForm();
                            })
                            .catch((err) => setError(err instanceof Error ? err.message : "Could not remove discount code"));
                        }}
                      >
                        Remove
                      </Button>
                    </Cluster>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Stack gap={3} className="admin-campaign-form">
            <FormField
              label="Code"
              tooltip="Exact string the buyer types on the pay page or in Plan & Billing."
            >
              <Input value={codeValue} onChange={(event) => setCodeValue(event.target.value)} aria-label="Discount code" />
            </FormField>
            <FormField
              label="Name"
              tooltip="Operator-facing label for this code in admin lists."
            >
              <Input value={codeName} onChange={(event) => setCodeName(event.target.value)} aria-label="Discount name" />
            </FormField>
            <FormField
              label="Hook"
              tooltip="Copy shown after the buyer successfully applies this code."
            >
              <Textarea
                value={codeHook}
                maxLength={400}
                rows={4}
                aria-label="Discount hook"
                onChange={(event) => setCodeHook(event.target.value)}
              />
            </FormField>
            <FormField
              label="Bound email"
              tooltip="Optional. Set it to issue the code to one customer: only that email can redeem it, once. Leave blank for a campaign code that any customer can use until the end date."
            >
              <Input
                type="email"
                value={codeEmail}
                onChange={(event) => setCodeEmail(event.target.value)}
                aria-label="Discount bound email"
              />
            </FormField>
            <Grid columns={2} gap={3}>
              <FormField
                label="Offer"
                tooltip="How the discount is calculated on the first invoice only."
              >
                <Select value={codeKind} onChange={(event) => setCodeKind(event.target.value)} aria-label="Discount offer">
                  <option value="percent_off">Percent off this payment</option>
                  <option value="free_months">Buy 12 months, get N free</option>
                </Select>
              </FormField>
              <FormField
                label={codeKind === "free_months" ? "Free months" : "Percent"}
                tooltip={
                  codeKind === "free_months"
                    ? "Extra months included when buying 12. Renewals bill full list price."
                    : "Percent off this payment only. Renewals bill full list price."
                }
              >
                <Input value={codeAmount} onChange={(event) => setCodeAmount(event.target.value)} aria-label="Discount value" />
              </FormField>
              {codeKind === "percent_off" ? (
                <FormField
                  label="For how many months"
                  tooltip="Optional, yearly billing only. Blank discounts the whole first payment. 6 means the percent applies to 6 of the 12 months, e.g. 20% off for 6 months is 10% off the year. Renewals bill full list price."
                >
                  <Input
                    value={codeMonths}
                    placeholder="Whole payment"
                    inputMode="numeric"
                    onChange={(event) => setCodeMonths(event.target.value)}
                    aria-label="Discount months"
                  />
                </FormField>
              ) : null}
              <FormField
                label="Start date"
                tooltip="First day this code can be redeemed (UTC date)."
              >
                <Input type="date" value={codeStarts} onChange={(event) => setCodeStarts(event.target.value)} aria-label="Discount start date" />
              </FormField>
              <FormField
                label="End date"
                tooltip="Last day this code stays redeemable. It auto-disables after this date or after use."
              >
                <Input type="date" value={codeEnds} onChange={(event) => setCodeEnds(event.target.value)} aria-label="Discount end date" />
              </FormField>
            </Grid>
            <Cluster gap={3} align="center">
              <Checkbox label="Basic" checked={codeStarter} onChange={(event) => setCodeStarter(event.target.checked)} />
              <Checkbox label="Pro" checked={codePro} onChange={(event) => setCodePro(event.target.checked)} />
              <Checkbox label="Enterprise" checked={codeEnt} onChange={(event) => setCodeEnt(event.target.checked)} />
            </Cluster>
            <Cluster gap={2}>
              <Button onClick={() => saveDiscountCode()}>{editingCodeId ? "Update code" : "Add code"}</Button>
              {editingCodeId ? (
                <Button variant="secondary" onClick={() => resetCodeForm()}>Cancel edit</Button>
              ) : null}
            </Cluster>
          </Stack>
        </Stack>
      </Card>
      <Card>
        <Stack gap={4}>
          <Stack gap={2}>
            <TipHeading
              label="Pricing page preview"
              tip="Draft of what public pricing cards will show after you publish. Live site reads the published snapshot only."
            />
            <p className="admin-muted">
              This is the draft the public pricing cards will show after you publish. The live site only reads
              the published snapshot. If that config is unavailable, it falls back to the built-in default hooks.
            </p>
            <p className="admin-muted">
              {planCopy?.published_at
                ? `Last published ${formatUtc(planCopy.published_at)}. ${planCopy.unpublished_changes ? "This draft has unpublished changes." : "Published copy matches this draft."}`
                : "Nothing published yet. The public page is using the built-in fallback hooks."}
            </p>
          </Stack>
          <div className="admin-plan-preview-grid">
            {plans.filter((plan) => plan.app_code === "crm").map((plan) => {
              const campaign = matchLiveCampaign(campaigns, plan.tier);
              const min = plan.min_seats ?? plan.seats;
              const maximum = plan.max_seats ? `${plan.max_seats} people` : "no maximum";
              const trialLine = plan.tier === "professional"
                ? `${plan.trial_days || 30}-day free trial`
                : "After your free trial";
              return (
                <article key={plan.tier} className={`admin-plan-preview-card${plan.tier === "professional" ? " is-featured" : ""}`}>
                  <h4>{plan.display_name ?? CAMPAIGN_PLAN_OPTIONS.find((item) => item.id === plan.tier)?.label ?? plan.tier}</h4>
                  <p className="admin-plan-preview-price">{previewMoney(plan)}</p>
                  <p className="admin-muted">per person / month</p>
                  <p className="admin-muted">minimum: {min} people<br />Maximum: {maximum}</p>
                  <p className="admin-muted">{trialLine}</p>
                  {campaign?.hook ? <p className="admin-plan-preview-hook">{campaign.hook}</p> : null}
                  <p className="admin-plan-preview-tagline">{PLAN_CARD_TAGLINES[plan.tier] || ""}</p>
                  <ul className="admin-plan-preview-features">
                    {(PLAN_CARD_FEATURES[plan.tier] || []).map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                  <Stack gap={2}>
                    <Button disabled>Start free trial</Button>
                    <Button variant="secondary" disabled>Buy now</Button>
                  </Stack>
                </article>
              );
            })}
          </div>
          <Cluster gap={2} align="center">
            <Button onClick={() => publishPlanCopy()} disabled={publishing}>
              {publishing ? "Publishing…" : "Publish to the pricing page"}
            </Button>
          </Cluster>
        </Stack>
      </Card>
    </Stack>
  );
}

function PlatformPage() {
  const [ai, setAi] = useState<PlatformAi | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [payments, setPayments] = useState<PaymentsToggle | null>(null);
  const [paymentsAccepted, setPaymentsAccepted] = useState(true);
  const [paymentsReason, setPaymentsReason] = useState("");
  const [paymentLeadWeeks, setPaymentLeadWeeks] = useState("1");
  const [paymentsError, setPaymentsError] = useState<string | null>(null);
  const [paymentsSaving, setPaymentsSaving] = useState(false);
  const [payfastStatus, setPayfastStatus] = useState<PayfastStatus | null>(null);
  const [payfastError, setPayfastError] = useState<string | null>(null);
  const [payfastSaving, setPayfastSaving] = useState(false);
  const [payfastSavedAt, setPayfastSavedAt] = useState<string | null>(null);
  const [payfastMerchantId, setPayfastMerchantId] = useState("");
  const [payfastMerchantKey, setPayfastMerchantKey] = useState("");
  const [payfastPassphrase, setPayfastPassphrase] = useState("");

  async function load() {
    try {
      const row = await api.get<{ ai: PlatformAi }>("/operator/platform");
      setAi(row.ai);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Platform settings could not be loaded.");
    }
    try {
      const toggle = await api.get<PaymentsToggle>("/operator/payments-toggle");
      setPayments(toggle);
      setPaymentsAccepted(toggle.accepted);
      setPaymentsReason(toggle.reason ?? "");
      setPaymentLeadWeeks(String(Math.max(0, Math.round((toggle.payment_lead_days ?? 7) / 7))));
    } catch (err) {
      setPaymentsError(err instanceof Error ? err.message : "Payments setting could not be loaded.");
    }
    try {
      const status = await api.get<PayfastStatus>("/operator/payfast-credentials");
      setPayfastStatus(status);
    } catch (err) {
      setPayfastError(err instanceof Error ? err.message : "PayFast status could not be loaded.");
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function savePaymentsToggle(accepted: boolean) {
    setPaymentsSaving(true);
    setPaymentsError(null);
    try {
      const row = await api.put<PaymentsToggle>("/operator/payments-toggle", {
        accepted,
        reason: paymentsReason.trim() || null,
        payment_lead_days: Math.max(0, Math.min(8, Number(paymentLeadWeeks) || 0)) * 7,
      });
      setPayments(row);
      setPaymentsAccepted(row.accepted);
      setPaymentsReason(row.reason ?? "");
      setPaymentLeadWeeks(String(Math.max(0, Math.round((row.payment_lead_days ?? 7) / 7))));
      notifySaved("Payments settings saved.");
    } catch (err) {
      setPaymentsError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setPaymentsSaving(false);
    }
  }

  async function savePayfastCredentials() {
    setPayfastSaving(true);
    setPayfastError(null);
    setPayfastSavedAt(null);
    try {
      await api.put("/operator/payfast-credentials", {
        payfast_merchant_id: payfastMerchantId.trim() || null,
        payfast_merchant_key: payfastMerchantKey.trim() || null,
        payfast_passphrase: payfastPassphrase.trim() || null,
      });
      setPayfastMerchantId("");
      setPayfastMerchantKey("");
      setPayfastPassphrase("");
      setPayfastSavedAt(new Date().toISOString());
      notifySaved("PayFast credentials saved.");
      const status = await api.get<PayfastStatus>("/operator/payfast-credentials");
      setPayfastStatus(status);
    } catch (err) {
      setPayfastError(err instanceof Error ? err.message : "PayFast credentials could not be saved.");
    } finally {
      setPayfastSaving(false);
    }
  }

  if (!ai) {
    return error ? <p className="admin-error">{error}</p> : <p>Loading…</p>;
  }

  return (
    <Stack gap={4}>
      <TipHeading
        as="h2"
        label="Accepting payments"
        tip="Platform-wide switch. When off, the marketing site and the in-app billing panel show checkout as unavailable, and the API refuses paid signups, plan changes, cancellations and scheduled-change reversals, and the card-update button is disabled. Trials and payments already in flight keep working."
      />
      {paymentsError && <Flash tone="error">{paymentsError}</Flash>}
      <Card>
        <Stack gap={3}>
          <Checkbox
            label="Payments enabled (customers can pay)"
            tooltip="Checked = payments are ON: the marketing Buy page and in-app billing take paid signups and plan changes. Unchecked = payments are OFF: checkout shows 'Payments unavailable' with the reason below, and the API refuses paid signups and plan changes. Save stores this switch, the reason, and the lead weeks."
            checked={paymentsAccepted}
            disabled={!payments || paymentsSaving}
            onChange={(event) => setPaymentsAccepted(event.target.checked)}
          />
          <FormField
            label="Reason shown to customers when off"
            tooltip="Optional. Shown on the marketing Buy page and the in-app Settings billing panel while payments are off."
          >
            <Input
              value={paymentsReason}
              disabled={!payments || paymentsSaving}
              onChange={(event) => setPaymentsReason(event.target.value)}
            />
          </FormField>
          <FormField
            label="Charge before renewal (weeks)"
            tooltip="PayFast yearly renewal payments are scheduled this many weeks before the customer's access renewal date. Monthly subscriptions always run on the agreed monthly date."
          >
            <Input
              type="number"
              min="0"
              max="8"
              step="1"
              value={paymentLeadWeeks}
              disabled={!payments || paymentsSaving}
              onChange={(event) => setPaymentLeadWeeks(event.target.value)}
            />
          </FormField>
          <Cluster gap={3} align="center">
            <Button
              variant="secondary"
              disabled={!payments || paymentsSaving}
              onClick={() => void savePaymentsToggle(paymentsAccepted)}
            >
              {paymentsSaving ? "Saving…" : "Save"}
            </Button>
            {payments?.updated_at ? (
              <p className="admin-muted">Last changed {formatUtc(payments.updated_at)}</p>
            ) : null}
          </Cluster>
        </Stack>
      </Card>

      <TipHeading
        as="h2"
        label="PayFast keys"
        tip="Writes into AWS Secrets Manager (saleslox-prod/payfast-credentials). Not a live toggle: the running api process caches settings at boot, so saved credentials only take effect after the next prod restart re-renders .env.prd from this secret."
      />
      {payfastError && <Flash tone="error">{payfastError}</Flash>}
      <Card>
        <Stack gap={3}>
          <Cluster gap={2} align="center">
            <span className={`admin-badge is-${payfastStatus?.configured ? "active" : "deactivated"}`}>
              {payfastStatus?.configured ? "Merchant credentials configured" : "No merchant credentials configured"}
            </span>
            <span className={`admin-badge is-${payfastStatus?.enabled ? "active" : "deactivated"}`}>
              {payfastStatus?.enabled ? "PAYFAST_ENABLED=true" : "PAYFAST_ENABLED=false"}
            </span>
            <span className={`admin-badge is-${payfastStatus?.payments_allowed ? "active" : "deactivated"}`}>
              {payfastStatus?.payments_allowed ? "Live HTTP allowed" : "Live HTTP blocked (not prod / sandbox)"}
            </span>
          </Cluster>
          <p className="admin-muted">
            PAYFAST_ENABLED and ENVIRONMENT are set on the EC2 host, not here — this form only writes the
            merchant credentials PayFast issues. Leave a field blank to keep its current stored value.
          </p>
          <FormField label="Merchant ID" tooltip="The numeric merchant id from the PayFast merchant dashboard.">
            <Input
              autoComplete="off"
              placeholder={payfastStatus?.configured ? "•••••••• (unchanged)" : "10000100"}
              value={payfastMerchantId}
              disabled={payfastSaving}
              onChange={(event) => setPayfastMerchantId(event.target.value)}
            />
          </FormField>
          <FormField label="Merchant key" tooltip="Never displayed back once saved.">
            <PasswordInput
              autoComplete="off"
              placeholder={payfastStatus?.configured ? "•••••••• (unchanged)" : "46f0cd694581a"}
              value={payfastMerchantKey}
              disabled={payfastSaving}
              onChange={(event) => setPayfastMerchantKey(event.target.value)}
            />
          </FormField>
          <FormField label="Passphrase" tooltip="Set on the merchant account's Integration settings in the PayFast dashboard.">
            <PasswordInput
              autoComplete="off"
              placeholder="••••••••"
              value={payfastPassphrase}
              disabled={payfastSaving}
              onChange={(event) => setPayfastPassphrase(event.target.value)}
            />
          </FormField>
          <Cluster gap={3} align="center">
            <Button
              variant="secondary"
              disabled={
                payfastSaving || (!payfastMerchantId.trim() && !payfastMerchantKey.trim() && !payfastPassphrase.trim())
              }
              onClick={() => savePayfastCredentials()}
            >
              {payfastSaving ? "Saving…" : "Save to Secrets Manager"}
            </Button>
            {payfastSavedAt ? (
              <p className="admin-muted">Saved {formatUtc(payfastSavedAt)} — restart prod to apply.</p>
            ) : null}
          </Cluster>
        </Stack>
      </Card>

      <p className="admin-muted">
        AI provider, key, cost rates, prompts and new-organization AI defaults are on the <Link to="/ai">AI</Link> page.
      </p>
      <CustomerAlertsCard />
    </Stack>
  );
}

const PLAN_LABELS: Record<string, string> = {
  starter: "Basic",
  professional: "Pro",
  enterprise: "Enterprise",
};

const CRM_PLAN_OPTIONS: Array<{
  tier: string;
  display_name: string;
  min_seats: number;
  max_seats: number | null;
}> = [
  { tier: "starter", display_name: "Basic", min_seats: 5, max_seats: 10 },
  { tier: "professional", display_name: "Pro", min_seats: 11, max_seats: 20 },
  { tier: "enterprise", display_name: "Enterprise", min_seats: 21, max_seats: null },
];

function crmPlanOptions(plans: PlanRow[]) {
  return CRM_PLAN_OPTIONS.map((fallback) => {
    const row = plans.find((plan) => plan.app_code === "crm" && plan.tier === fallback.tier);
    return {
      ...fallback,
      display_name: row?.display_name ?? fallback.display_name,
      min_seats: row?.min_seats ?? fallback.min_seats,
      max_seats: row?.max_seats === undefined ? fallback.max_seats : row.max_seats,
    };
  });
}

function seatsForPlan(
  plan: { min_seats: number; max_seats: number | null },
  currentSeats: number,
): string {
  if (Number.isInteger(currentSeats) && currentSeats >= plan.min_seats && (plan.max_seats == null || currentSeats <= plan.max_seats)) {
    return String(currentSeats);
  }
  return String(plan.min_seats);
}

function planName(tier: string | null | undefined): string {
  if (!tier) return "";
  return PLAN_LABELS[tier] ?? tier;
}

function billingFrequencyLabel(interval: string | null | undefined): string | null {
  if (interval === "year") return "Yearly";
  if (interval === "month") return "Monthly";
  if (interval === "exempt") return "Billing exempt";
  return null;
}

function historyChange(row: PlanHistoryRow): string {
  const to = planName(row.to_tier) || row.kind;
  const from = planName(row.from_tier);
  const seats = row.seats ? ` · ${row.seats} users` : "";
  const paid = row.amount_cents != null ? ` · ${money(row.amount_cents, row.currency || "USD")}` : "";
  const on = (iso: string | null | undefined) => (iso ? formatDateUtc(iso) : "—");
  if (row.kind === "plan_change" && row.upgrade_charged_now && row.prorated_months != null) {
    const currency = row.currency || "USD";
    const current = money(row.proration_current_annual_cents || 0, currency);
    const next = money(row.proration_new_annual_cents || 0, currency);
    const subtotal = money(row.proration_subtotal_cents ?? row.amount_cents ?? 0, currency);
    const total = money(row.proration_total_cents ?? row.amount_cents ?? 0, currency);
    const tax = row.proration_tax_cents ? ` + ${money(row.proration_tax_cents, currency)} ${row.tax?.label || "tax"}` : "";
    return `Prorated upgrade ${from ? `${from} → ` : ""}${to}${seats}: ((${next} - ${current}) / 12) × ${row.prorated_months} = ${subtotal}${tax} = ${total}. Features unlocked now; upgraded billing starts ${on(row.billing_effective_at)}.`;
  }
  if (row.kind === "signup") return `Started ${to}${seats}`;
  if (row.kind === "payment") return `Paid ${to}${seats}${paid} · through ${on(row.period_end)}`;
  if (row.kind === "renewal") return `Renewed ${to}${seats}${paid} · through ${on(row.period_end)}`;
  if (row.kind === "payment_failed") return `Payment failed${paid}${row.error_message ? ` · ${row.error_message}` : ""}`;
  if (row.kind === "plan_change_scheduled") return `Scheduled ${from ? `${from} → ` : ""}${to}${seats} from ${on(row.effective_at)}`;
  if (row.kind === "plan_change_reverted") return "Scheduled plan change withdrawn";
  if (row.kind === "resubscribe") return "Resubscribed";
  if (row.kind === "subscription_ended") return `Subscription ended${row.reason ? ` (${row.reason})` : ""}`;
  if (row.kind === "cancel") return row.scheduled ? `Cancelled · access until ${on(row.access_until)}` : "Cancelled";
  if (row.source === "billing_exempt") {
    if (from && from !== to) return `Billing exempt · ${from} → ${to}${seats}`;
    return `Billing exempt · ${to}${seats}`;
  }
  if (from) return `${from} → ${to}${seats}`;
  return `${to}${seats}`;
}

function historyDiscount(row: PlanHistoryRow): string {
  if (!row.discount_code) return "—";
  const parts = [row.discount_code];
  if (row.discount_kind === "percent_off" && row.discount_months && row.discount_value) {
    parts.push(`${row.discount_value}% off for ${row.discount_months} months`);
    if (row.percent_applied) parts.push(`${row.percent_applied}% off the year`);
  } else if (row.discount_kind === "free_months" && row.discount_value) {
    parts.push(`Buy 12, get ${row.discount_value} free`);
  } else if (row.percent_applied) parts.push(`${row.percent_applied}% off`);
  if (row.list_cents != null && row.pay_cents != null && row.pay_cents !== row.list_cents) {
    parts.push(`${money(row.pay_cents, row.currency || "USD")} of ${money(row.list_cents, row.currency || "USD")}`);
  }
  return parts.join(" · ");
}

function historyPaymentId(row: PlanHistoryRow): string {
  return row.refund_id || row.pf_payment_id || row.payment_intent_id || row.charge_id || row.invoice_id || "";
}

function usedDiscountCodes(detail: Pick<OrgDetail, "discount_codes" | "plan_history">): string[] {
  if (detail.discount_codes?.length) return detail.discount_codes;
  const seen: string[] = [];
  for (const row of detail.plan_history ?? []) {
    const code = (row.discount_code || "").trim();
    if (code && !seen.includes(code)) seen.push(code);
  }
  return seen;
}

function nurtureStepLabel(label: string, at: string | null | undefined, source: string | undefined): string {
  if (!at) return `${label} sent`;
  const when = formatUtc(at);
  if (source === "purchase") return `${label} skipped (purchased) · ${when}`;
  if (source === "manual") return `${label} marked sent by staff · ${when}`;
  return `${label} sent · ${when}`;
}

function statusLabel(status: string): string {
  if (status === "active") return "Active";
  if (status === "suspended" || status === "disabled" || status === "deactivated") return "Disabled";
  return status;
}

function crmLicenceLapsed(crm: LicenseRow | undefined): boolean {
  if (!crm) return false;
  if (crm.grant_source === "pending_provider" || crm.status === "suspended") return false;
  if (crm.status === "expired") return true;
  if (!crm.access_ends_at) return false;
  const ends = Date.parse(crm.access_ends_at);
  return Number.isFinite(ends) && ends <= Date.now();
}

function displayedStatusLabel(org: OrgRow): string {
  if (org.status === "active" && org.cancelled_at) return "Cancelled";
  const crm = org.licenses.find((lic) => lic.app_code === "crm");
  if (org.status === "active" && crmLicenceLapsed(crm)) return "Expired";
  return statusLabel(org.status);
}

function CustomerStatus({
  status,
  cancelledAt,
  licences,
  currentPlan,
  showWhen = false,
  className,
}: {
  status: string;
  cancelledAt?: string | null;
  licences: LicenseRow[];
  currentPlan?: LicenseRow | null;
  showWhen?: boolean;
  className?: string;
}) {
  const crm = currentPlan?.app_code === "crm"
    ? currentPlan
    : licences.find((lic) => lic.app_code === "crm");
  const flag = className ? ` ${className}` : "";
  if (status === "active" && cancelledAt) {
    return (
      <>
        <span className={`admin-badge is-cancelled${flag}`}>Cancelled</span>
        {showWhen ? (
          <div className="admin-muted">
            Active until {formatDateUtc(crm?.access_ends_at)}
          </div>
        ) : null}
      </>
    );
  }
  if (status === "active" && crmLicenceLapsed(crm)) {
    return (
      <>
        <span className={`admin-badge is-expired${flag}`}>Expired</span>
        {showWhen && crm?.access_ends_at ? (
          <div className="admin-muted">Ended {formatDateUtc(crm.access_ends_at)}</div>
        ) : null}
      </>
    );
  }
  return <StatusBadge status={status} className={className} />;
}

function statusClass(status: string): string {
  if (status === "active") return "active";
  if (status === "suspended" || status === "disabled" || status === "deactivated") return "disabled";
  return status;
}

function StatusBadge({ status, className }: { status: string; className?: string }) {
  return <span className={`admin-badge is-${statusClass(status)}${className ? ` ${className}` : ""}`}>{statusLabel(status)}</span>;
}

function SortableHeader({
  label,
  column,
  sortKey,
  sortDir,
  onSort,
}: {
  label: string;
  column: CustomerSortKey;
  sortKey: CustomerSortKey;
  sortDir: "asc" | "desc";
  onSort: (column: CustomerSortKey) => void;
}) {
  const active = sortKey === column;
  return (
    <th aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}>
      <button type="button" className="admin-sort-btn" onClick={() => onSort(column)}>
        {label}
      </button>
    </th>
  );
}

function orgLicenseLabel(org: OrgRow): string {
  return org.licenses.map((lic) => `${lic.app_code} ${lic.tier}`).join(", ") || "None";
}

function orgLicenseType(org: OrgRow): "trial" | "paid" | "exempt" | "" {
  if (org.license_type === "trial" || org.license_type === "paid" || org.license_type === "exempt") return org.license_type;
  const crm = org.licenses.find((item) => item.app_code === "crm") ?? org.licenses[0];
  if (!crm) return "";
  if (crm.grant_source === "trial") return "trial";
  if (crm.grant_source === "billing_exempt") return "exempt";
  return "paid";
}

function hasActivePaidSubscription(row: Pick<OrgRow, "licenses" | "cancelled_at"> | Pick<OrgDetail, "licenses" | "current_plan" | "cancelled_at">): boolean {
  const licenses = "current_plan" in row && row.current_plan ? [row.current_plan, ...row.licenses] : row.licenses;
  return !row.cancelled_at && licenses.some((lic) => (
    lic.app_code === "crm"
    && lic.status === "active"
    && lic.grant_source !== "trial"
    && lic.grant_source !== "operator_override"
    && lic.grant_source !== "billing_exempt"
  ));
}

function licenseTypeLabel(type: "trial" | "paid" | "exempt" | ""): string {
  if (type === "trial") return "Trial";
  if (type === "paid") return "Paid";
  if (type === "exempt") return "Exempt";
  return "—";
}

function attributionLabel(attr: SignupAttribution | null | undefined): string {
  const source = (attr?.source || "").trim();
  if (!source) return "Direct";
  const medium = (attr?.medium || "").trim();
  return medium ? `${source} · ${medium}` : source;
}

function attributionSummary(attr: SignupAttribution | null | undefined): string {
  const parts = [attributionLabel(attr)];
  if (attr?.campaign) parts.push(attr.campaign);
  if (attr?.landing) parts.push(attr.landing);
  if (attr?.referrer_host) parts.push(attr.referrer_host);
  return parts.join(" · ");
}

function compareCustomers(
  a: OrgRow,
  b: OrgRow,
  sortKey: CustomerSortKey,
  sortDir: "asc" | "desc",
): number {
  const dir = sortDir === "asc" ? 1 : -1;
  const text = (value: string | null | undefined) => (value ?? "").toLocaleLowerCase();
  const createdMs = (org: OrgRow) => {
    const ms = Date.parse(org.created_at || "");
    return Number.isFinite(ms) ? ms : 0;
  };
  let result = 0;
  switch (sortKey) {
    case "organization":
      result = text(a.display_name).localeCompare(text(b.display_name));
      break;
    case "slug":
      result = text(a.slug).localeCompare(text(b.slug));
      break;
    case "status":
      result = text(displayedStatusLabel(a)).localeCompare(text(displayedStatusLabel(b)));
      break;
    case "created":
      result = createdMs(a) - createdMs(b);
      break;
    case "people":
      result = a.members - b.members;
      break;
    case "storage":
      result = (a.storage_mb ?? 0) - (b.storage_mb ?? 0);
      break;
    case "licences":
      result = text(orgLicenseLabel(a)).localeCompare(text(orgLicenseLabel(b)));
      break;
    case "license_type":
      result = text(licenseTypeLabel(orgLicenseType(a))).localeCompare(text(licenseTypeLabel(orgLicenseType(b))));
      break;
    case "source":
      result = text(attributionLabel(a.signup_attribution)).localeCompare(text(attributionLabel(b.signup_attribution)));
      break;
    case "discount":
      result = text((a.discount_codes ?? []).join(" ")).localeCompare(text((b.discount_codes ?? []).join(" ")));
      break;
  }
  if (result === 0 && sortKey !== "organization") {
    result = text(a.display_name).localeCompare(text(b.display_name));
  }
  return dir * result;
}
